import { AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output, inject, DestroyRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash-es';
import { environment } from '../../../environments/environment';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { GridFactory } from '../../core/grid/services/grid.service';
import { GridSaveFactoryService } from '../../core/grid/services/grid-save-factory.service';
import { GRID_SCAFFOLD_HTML } from '../../core/grid/grid-scaffold';
import { EfileGridCacheService } from './efile-grid-cache.service';
import { ModalFactoryService } from '../../global/services/modal-factory.service';
import { ComponentRegistryService } from '../../core/grid/services/component-registry.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import {
  CeHostDialogComponent,
  CeHostDialogData,
  ModalLockPayload,
} from '../../shared/components/ce-host-dialog/ce-host-dialog.component';
import { buildEfileCeHostDialogData } from './efile-ce-dialog-context';

declare var $: any;

export interface EfileGridConfig {
  /** Numeric action_id (used in loadGridJson?action_id=...). Provide either actionId or actionCode. */
  actionId?: number;
  /** String action_code (used in loadGridJson?action_code=...). Takes precedence over actionId when set. */
  actionCode?: string;
  /**
   * Legacy `grid-tpl.html` / `EfileCustomGrid` export shape (return-review
   * tabs, scrubbing run/history, etc.): `action_id` matches load (or
   * `exportActionId` when load uses `action_code`), `screen_key` from
   * `exportScreenKey`, jsonObj =
   * `{ submission_id, scenario_id, …filterParams, display_* }` — no
   * `isFileDownloadRequest` / workspace `globalParams` merge.
   */
  legacyGridTplExport?: boolean;
  /**
   * Legacy efile custom grids use a different identifier for export than for
   * load (`#action_id` hidden span vs `loadGridJson?action_code=…`). When set,
   * `exportToExcel` always sends `action_id=<exportActionId>`.
   * When combined with {@link legacyGridTplExport}, still uses grid-tpl jsonObj.
   */
  exportActionId?: number | string;
  /**
   * Legacy efile custom grids hard-code `#screen_key` per grid template for
   * export (e.g. formelements-report → 11550). Falls back to active workspace
   * screen when omitted.
   */
  exportScreenKey?: number | string;
  /**
   * Some legacy efile grids load with one `grid_id` but export with another
   * (`#grid_id` hidden span). Example: key-xml-gen loads 6127, exports 6074.
   */
  exportGridId?: number | string;
  /**
   * Legacy hidden-span defaults for custom-grid export `jsonObj`
   * (`#display_all_me_code`, `#display_scenario`, `#display_filingGrp`).
   */
  exportDisplayDefaults?: {
    display_mE_code?: string;
    display_scenario_description?: string;
    display_filing_group_description?: string;
  };
  gridId: string;
  /** Extra params merged into every loadGridData call (e.g. tax_year, type, client_key). */
  extraParams?: Record<string, any>;
  /** If true, data is loaded automatically after grid init. Default: true. */
  autoLoad?: boolean;
  /**
   * Cache key used by EfileGridCacheService.
   * When set, the directive caches data responses and restores them on re-init.
   * Typically set to the route path (e.g. 'efile/submissions-dashboard').
   */
  cacheKey?: string;
  /**
   * When true, the first auto-load after gizmoGrid init always hits the server
   * (equivalent to calling refresh) instead of restoring from cacheKey.
   * Use for hosts that destroy/remount the grid on tab switches but still
   * need fresh data every visit (e.g. efile scrubbing).
   */
  skipCacheOnInitialLoad?: boolean;
  /**
   * Request params that count as user filter criteria (Run Scrubbing
   * `form_key` / `search_key`, History `search_key`). When set, the grid
   * does not auto-load. Legacy `EfileCustomGrid` only calls `getData` on
   * Filter / refresh when the screen has filters; an automatic empty
   * `loadGridJson` makes gizmoGrid show "No data found with this filter
   * criteria" before the user has filtered. A user `refresh()` still loads,
   * and an empty filtered result keeps that alert.
   */
  emptyAlertFilterParams?: string[];
  /**
   * When `true`, toolbar Export to Excel is delegated to the host via
   * `excelExportRequested` (e.g. grids fed by a custom API instead of
   * `loadGridJson`). The host must call `finishExcelExport()` when done.
   */
  delegateExcelExport?: boolean;
  /**
   * When `true`, gizmoGrid `form` / `modal` / `openGridModal` callbacks are
   * routed through the same `CeHostDialogComponent` flow that the core
   * workspace `GridDirective` uses for legacy CE-based modals — i.e. row
   * action click → `ComponentRegistryService.resolve(callName)` (or
   * `ModalFactoryService.getModal()` fallback) → `MatDialog.open(
   * CeHostDialogComponent, { data: { ceTagName, rowData, colData,
   * headerData, baseUrls, filterParams, ... } })`.
   *
   * `gridCallback` is still emitted afterwards so hosts that want to
   * observe the click (analytics, refresh hooks, etc.) can do so.
   *
   * Default `false` so existing efile hosts that already manage their own
   * modals (e.g. `efile-overrides`, `efile-scrubbing`, `efile-return-review`)
   * are not affected. Opt-in screen by screen.
   */
  autoOpenCeDialog?: boolean;
}

export interface EfileGridCallbackEvent {
  callName: string;
  data: any;
  type: string;
}

/**
 * Reusable gizmoGrid directive for efile screens. Direct twin of
 * `AdminGridDirective` — kept separate so admin and efile screens do not
 * share cache keys and so the efile team can evolve callback routing
 * independently.
 *
 * Usage:
 *   <div efileGrid [gridConfig]="gridConfig"
 *        (gridCallback)="onGridCallback($event)"
 *        (loadingStateChange)="loading = $event"
 *        class="gizmo-grid"></div>
 *
 * Host responsibilities:
 *   1. Provide an EfileGridConfig object
 *   2. Handle custom callbacks (row click, form, ctrlFn, etc.)
 *   3. Call clearCache() + refresh(overrides) when filters change
 */
@Directive({
    selector: '[efileGrid]',
    exportAs: 'efileGrid',
    standalone: false
})
export class EfileGridDirective implements AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Input() gridConfig!: EfileGridConfig;

  /** Fires for every gizmoGrid callback. */
  @Output() gridCallback = new EventEmitter<EfileGridCallbackEvent>();

  /** Fires after processAndLoadData completes (both fresh and cached). */
  @Output() gridDataLoaded = new EventEmitter<any>();

  /** Emits true when data fetch starts, false when it completes or fails. */
  @Output() loadingStateChange = new EventEmitter<boolean>();

  /** Emitted when `delegateExcelExport` is set and the user clicks Export to Excel. */
  @Output() excelExportRequested = new EventEmitter<'all' | 'filtered'>();

  private ggApi: any = null;
  private loadSub?: Subscription;
  private downloadExcelSub?: Subscription;
  private gridInitialized = false;
  private destroyed = false;
  /** `ngAfterViewInit` defers `initGrid` by a macrotask. Cancel it on destroy so a host that is recreated while the route settles does not leave the detached instance to POST `loadGridJson` as well. */
  private initTimer: ReturnType<typeof setTimeout> | undefined;
  private lastLoadedData: any = null;

  // ---------------------------------------------------------------------------
  // Directive-managed loader overlay
  //
  // The legacy gizmoGrid loader (`base.elements.dataLoader` /
  // `loadingTemplate` — gizmoGrid.js L8634, sized via L1401-L1402 to
  // `width: $el.width() + 30, height: $(window).height() - $el.offset().top`)
  // is unreliable inside efile hosts:
  //
  //   1. Submission List Return Review steps mount the directive host
  //      `[hidden]` until a step is selected; `applyFilters()` /
  //      `grid.refresh()` then call `showLoader()` on a host that's
  //      still being measured by jQuery as `0×0` because of the in-flight
  //      `display: none → block` transition. The native loader paints
  //      offscreen or with zero height and the user never sees it.
  //   2. Window-relative height math (`$(window).height() - host.top`)
  //      breaks inside scrollable / flex-bounded ancestors (`.step-content-wrap`
  //      sets `overflow: auto`); the loader rect ends up either clipped by
  //      the host's overflow box or extended past the visible area into
  //      regions covered by the workspace tab strip.
  //
  // To get the same visual we ship in submission-data — translucent gray
  // backdrop + centred AppKit spinner over the grid pane — every efile
  // grid gets a directive-managed overlay that:
  //   - lives *inside* the host element (sized to `inset: 0` of the host,
  //     not `$(window)`), so it always covers exactly the grid pane
  //   - paints above the gizmoGrid scaffold (`z-index: 700`, above
  //     gizmoGrid's `.gg-loader` `z-index: 640`)
  //   - is toggled on `showLoader()` / `hideLoader()` so all existing call
  //     sites (refresh button, applyFilters, fetchAndLoad, processAndLoadData
  //     empty path, error handler) stay correct
  //   - coexists with submission-data's own `.gg-host-wrap.gg-loading`
  //     wrapper: that wrapper does `.gg-host { visibility: hidden }`, which
  //     also suppresses our intra-host overlay so the wrapper-level
  //     spinner is the only one the user sees on Submission List
  // ---------------------------------------------------------------------------
  private loaderOverlayEl: HTMLElement | null = null;
  private hostOriginalPosition: string | null = null;
  /** Queued until `initGrid()` finishes (custom API hosts mount with `autoLoad: false`). */
  private pendingPayload: any = null;
  /**
   * Floating gizmoGrid overlays we re-parent to <body> in `initGrid` so their
   * `position: absolute + pageX/pageY` math (gizmoGrid.js L8350-L8369) lands on
   * document coordinates instead of the host's positioned bounding box. Tracked
   * here so `destroyGrid` can detach them and avoid leaking nodes between
   * mount/unmount cycles (e.g. tab switches).
   */
  private detachedOverlays: any[] = [];

  // ---------------------------------------------------------------------------
  // Inline-edit positioning fix
  //
  // gizmoGrid's `textAreaEdit` (gizmoGrid.js L7383-L7384) writes `top`/`left`
  // on `.edit-grid-value` using `$(cell).offset()` — i.e. DOCUMENT
  // coordinates. The overlay is `position: absolute`, so the browser resolves
  // `top`/`left` against the overlay's **offsetParent** (nearest positioned /
  // transformed ancestor), not the document.
  //
  // We used to subtract only `this.el.nativeElement` (`.gg-host`) document
  // offset. That breaks when a higher ancestor establishes the containing
  // block — e.g. Angular Material `<mat-tab-group>` uses transforms on tab
  // bodies; the offsetParent is then that wrapper, not `.gg-host`. Using the
  // host offset in that case leaves the editor shifted (often up into the tab
  // strip while the cell highlight is still on the grid).
  //
  // Fix: subtract `$(overlay).offsetParent().offset()` (jQuery) for the **actual**
  // offsetParent and add that element's scrollTop/Left. Double rAF from the
  // MutationObserver so layout settles before we read coordinates.
  //
  // `suppressEditorRealign` breaks the loop our own style write would create.
  // ---------------------------------------------------------------------------
  private editorObserver?: MutationObserver;
  private suppressEditorRealign = false;
  /** Same object reference passed to `gizmoGrid.loadData({ data })` — `selected` stays in sync with the plugin. */
  private lastGridDataPayload: any = null;

  // ---------------------------------------------------------------------------
  // Host layout sync (bottom whitespace + horizontal-scroll fix).
  //
  // Two long-standing layout symptoms in the Submission List custom grid:
  //
  //   1. Whitespace below `.gg-footer`. The submission shell wraps the grid
  //      in `.gg-host-wrap` and pins it with a hard `height: calc(100vh -
  //      200px)`. The 200px constant assumes a fixed amount of chrome above
  //      the grid (workspace header + tab strip + filter row), but the real
  //      chrome height varies with the active route, breadcrumb wrapping,
  //      filter-chip overflow, etc. When the constant overshoots, the
  //      wrapper renders shorter than the actual available viewport and
  //      leaves a blank band below the grid.
  //
  //   2. Right-scroll columns missing. gizmoGrid `buildHScrollBar()` snapshots
  //      `base.$el.width()` and `vtrack.width()` to compute the horizontal
  //      scroll window. If the host width is stale when data lands (Angular
  //      flex hasn't settled, sidebar still animating, fonts still loading),
  //      the dragger maps to the wrong window and scrolling right paints
  //      blank columns instead of the trailing fields (`Zip`, `XML`,
  //      `Submission Date`, `Return Status`).
  //
  // Fix:
  //   - `syncHostWrapperHeight()` measures the actual viewport space below
  //     the wrapper's top edge and writes the host wrapper height inline,
  //     overriding the SCSS calc only when the wrapper is present (other
  //     efile hosts that don't use `.gg-host-wrap` are untouched).
  //   - `triggerGridResize()` calls gizmoGrid's public `base.resize()` so
  //     `buildGrid()` recomputes header / body / hScroll / footer in one
  //     pass with the freshly-bounded host.
  //   - A `ResizeObserver` on the host wrapper kicks `triggerGridResize()`
  //     whenever an ancestor flex layout shifts (sidebar toggle, mat-tab
  //     mount, window resize) so the horizontal-scroll math always reflects
  //     the live host width.
  //
  // Scoped to `[efileGrid]` only — common grid scaffold and admin grid
  // directive are not touched.
  // ---------------------------------------------------------------------------
  private resizeObserver?: ResizeObserver;
  private resizeRafScheduled = false;
  private lastSyncedHostHeight = 0;
  /** Watches this host's column DOM so the end-of-scroll gap can be closed. */
  private endGapObserver?: MutationObserver;
  private endGapRaf = 0;

  constructor(
    private el: ElementRef,
    private appService: AppService,
    private globalService: GlobalService,
    private gridFactory: GridFactory,
    private gridSaveFactory: GridSaveFactoryService,
    private cacheService: EfileGridCacheService,
    private zone: NgZone,
    private dialog: MatDialog,
    private modalFactory: ModalFactoryService,
    private componentRegistry: ComponentRegistryService,
    private gtwSnackbarService: GtwSnackbarService,
    private workspaceService: WorkspaceService,
    private http: HttpClient,
  ) {}

  /** Expose the raw gizmoGrid API for advanced use. */
  get api(): any {
    return this.ggApi;
  }

  get isInitialized(): boolean {
    return this.gridInitialized;
  }

  // ---------------------------------------------------------------------------
  // Custom-screen context resolution
  //
  // Background: efile screens that drive their data via a custom action
  // code (e.g. add-edit screen → `s3hr77` load + `rv8nq0` save, override
  // modal → `rv8nq0` save) need `activity_key` + `screen_key` +
  // `jcd_ta_key` (and downstream `screen_type` + `jcd_key`) on every API
  // request. Legacy AngularJS controllers had this for free because
  // `JsonObjectFactory.getJSONObj` always merged `GlobalService.globalParams`
  // (which the workspace populated from the active route) and
  // `workspaceFactory.activeScreen.filters.getFilterParams()` into the
  // outgoing payload.
  //
  // In the migrated stack the directive owns the load/launch lifecycle
  // for these screens, so it has to take responsibility for plumbing the
  // same envelope. This helper is the single source of truth for those
  // five keys and is consumed by:
  //   1. `fetchAndLoad` — merged into the `loadGridJson` request body so
  //      backend SP_NAME lookups (`activity_key`, `screen_key`, `jcd_ta_key`)
  //      succeed.
  //   2. `prepareDialogData` — folded into `gridExtraParams` so any CE
  //      modal launched from a row click (add-edit, override-modal,
  //      xml-vs-elements, etc.) sees the keys via its `filter-params`
  //      and `header-data` inputs and can build its own
  //      `s3hr77`/`rv8nq0` payload without re-deriving them.
  //
  // Resolution order, highest first:
  //   - `gridConfig.extraParams.<key>`         (host explicit override)
  //   - `workspaceFactory.activeScreen.<key>`  (live custom-screen route)
  //   - `globalService.global.globalParams.<key>` (workspace envelope)
  //
  // Mirrors the precedence used by `add-edit.component.ts.buildHierarchyParams`
  // (`f` → `g` → `customCtx`) so the directive and the launched modal
  // converge on the same value regardless of which side resolves first.
  // ---------------------------------------------------------------------------
  private static readonly CUSTOM_SCREEN_CONTEXT_KEYS = [
    'activity_key',
    'screen_key',
    'jcd_ta_key',
    'screen_type',
    'jcd_key',
  ] as const;

  /**
   * Snapshot of the five custom-screen keys for the current
   * `gridConfig` + active workspace screen. Returned object is a fresh
   * shallow copy; safe to splat into params/extraParams.
   */
  resolveCustomScreenContext(): Record<string, any> {
    const cfg = this.gridConfig;
    const extra = cfg?.extraParams || {};
    const activeScreen = this.workspaceService?.workspaceFactory?.activeScreen || {};
    const globalParams = this.globalService?.global?.globalParams || {};

    const ctx: Record<string, any> = {};
    for (const key of EfileGridDirective.CUSTOM_SCREEN_CONTEXT_KEYS) {
      const upper = key.toUpperCase();
      const candidates = [
        extra[key],
        extra[upper],
        (activeScreen as any)?.[key],
        (activeScreen as any)?.[upper],
        (activeScreen as any)?.parent?.[key],
        (activeScreen as any)?.parent?.[upper],
        (globalParams as any)?.[key],
        (globalParams as any)?.[upper],
      ];
      for (const v of candidates) {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          ctx[key] = v;
          break;
        }
      }
    }
    return ctx;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Suppress the browser's native right-click menu anywhere inside the grid
   * host so users see ONLY gizmoGrid's own context menu (`.gg-context-menu`).
   *
   * Without this, right-clicking on the grid surface produced both menus
   * stacked on top of each other (gizmoGrid only attaches its `contextmenu`
   * handler to data cells / specific row regions, so empty toolbar / scroll
   * gutter / header areas fell through to the browser default).
   *
   * `preventDefault()` is enough — gizmoGrid's own handler still runs because
   * it lives on inner `<td>` / row elements which fire BEFORE this host-level
   * listener (capture: false → bubbling phase, deepest target first).
   */
  @HostListener('contextmenu', ['$event'])
  onHostContextMenu(_event: Event): void {
    const event = _event as MouseEvent;
    event.preventDefault();
    setTimeout(() => this.alignContextMenu(event), 0);
  }

  @HostListener('mousedown', ['$event'])
  onHostMouseDown(_event: Event): void {
    const event = _event as MouseEvent;
    if (event.button !== 2) return;
    // gizmoGrid builds its context menu from a delayed right-button mousedown.
    // Align after that callback has populated and shown `.gg-context-menu`.
    setTimeout(() => this.alignContextMenu(event), 25);
  }

  /**
   * Re-pin the gizmoGrid loader to the data viewport whenever the window
   * resizes. The hard cases are:
   *   - submission-detail sidebar toggle (changes the host's left/width)
   *   - browser window resize while a refresh is in flight
   *   - lazy-mounting a return-review tab inside a previously-hidden
   *     container (the grid body grows after `display: none` is removed)
   * Without this, a long-running fetch's loader can drift away from
   * the actual data area mid-flight.
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.gridInitialized) return;
    // Re-bound the host wrapper + force gizmoGrid to recompute header / body /
    // hScroll widths so the right-side columns stay reachable after the
    // viewport changes (DevTools open, browser resize, sidebar toggle).
    // gizmoGrid's own window-resize handler re-runs the loader's
    // `setSize()` so we don't need to touch the loader rect ourselves.
    this.scheduleLayoutSync();
  }

  ngAfterViewInit(): void {
    this.downloadExcelSub = this.workspaceService.downloadExcel.subscribe(() => {
      // Legacy efile custom grids (setup tabs) export via the gizmo toolbar
      // only — their payload uses per-grid #action_id / #screen_key, not the
      // workspace filter-bar Excel flow.
      if (!this.isLegacyCustomGridExport()) {
        this.exportToExcel();
      }
    });
    this.gridSaveFactory.gridRefreshNeeded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.gridInitialized) {
          this.refresh();
        }
      });
    this.initTimer = setTimeout(() => {
      this.initTimer = undefined;
      if (this.destroyed) {
        return;
      }
      this.initGrid();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.initTimer !== undefined) {
      clearTimeout(this.initTimer);
      this.initTimer = undefined;
    }
    if (this.downloadExcelSub) {
      this.downloadExcelSub.unsubscribe();
      this.downloadExcelSub = undefined;
    }
    this.destroyGrid();
  }

  // ---------------------------------------------------------------------------
  // Public API — callable from the host component via template reference
  // ---------------------------------------------------------------------------

  /**
   * Load (or reload) grid data using the current gridConfig.
   * Optionally pass extra params that override gridConfig.extraParams
   * for this single call.
   */
  loadData(overrideParams?: Record<string, any>): void {
    this.fetchAndLoad(overrideParams, false);
  }

  /** Force reload — ignores cache. `userRefresh` starts a new POST; the initial mount reuses an in-flight read. */
  refresh(overrideParams?: Record<string, any>): void {
    this.fetchAndLoad(overrideParams, true, true);
  }

  /**
   * Load a pre-built gizmoGrid payload (`header`, `data`, `settings`, …)
   * without calling `loadGridJson`. Used by screens backed by custom APIs
   * (e.g. IRS XML element comparison).
   */
  loadPayload(data: any): void {
    if (!data) {
      return;
    }
    if (!this.ggApi || !this.gridInitialized) {
      this.pendingPayload = data;
      return;
    }
    this.pendingPayload = null;
    this.showLoader();
    this.loadingStateChange.emit(true);
    this.processAndLoadData(data);
    this.hideLoader();
    this.loadingStateChange.emit(false);
  }

  private flushPendingPayload(): void {
    if (!this.pendingPayload || !this.ggApi || !this.gridInitialized) {
      return;
    }
    const payload = this.pendingPayload;
    this.pendingPayload = null;
    this.showLoader();
    this.loadingStateChange.emit(true);
    this.processAndLoadData(payload);
    this.hideLoader();
    this.loadingStateChange.emit(false);
  }

  /** Clear the cache entry for this grid (if cacheKey is set). */
  clearCache(): void {
    if (this.gridConfig?.cacheKey) {
      this.cacheService.clear(this.gridConfig.cacheKey);
    }
  }

  /** Re-measure host + rebuild gizmoGrid layout after the host becomes visible. */
  relayout(): void {
    this.scheduleLayoutSync();
  }

  /** Live width of the grid host element (for column stretch calculations). */
  getHostClientWidth(): number {
    const el = this.el?.nativeElement as HTMLElement | null;
    return el?.clientWidth ?? 0;
  }

  /**
   * Show a directive-managed loader overlay (translucent backdrop +
   * centred AppKit spinner) over the grid host while keeping
   * gizmoGrid's native `showLoader()` as a no-op pass-through guard for
   * any third-party code that might be observing the legacy `.gg-loader`
   * visibility.
   *
   * Background: gizmoGrid's native loader (`base.elements.dataLoader`,
   * gizmoGrid.js L1401-L1402) sizes itself to
   * `width: $el.width() + 30, height: $(window).height() - $el.offset().top`
   * and lives at `position: absolute; left: 94px; top: ?` against the
   * host's offsetParent. That works on Admin grids where the host has
   * fixed bounded geometry, but the efile shells break it in two ways:
   *
   *   1. **Loader invisible on gated steps** — `applyFilters()` calls
   *      `grid.refresh()` → `showLoader()` synchronously while the host
   *      is still `[hidden]` (display:none). `$(window).height() -
   *      offset.top` resolves to a tiny / negative number so the loader
   *      paints offscreen or with zero height. Even when the host
   *      becomes visible the next frame, the dimensions stay stale
   *      until something else triggers `setSize()`.
   *   2. **Loader half-visible on flex hosts** — Return Review's
   *      `.step-content-wrap` is `overflow: auto`. The native loader's
   *      window-relative height routinely exceeds the visible host area
   *      and gets clipped, leaving a sliver of gray that doesn't read
   *      as a loader to the user.
   *
   * Submission-data avoids both by wrapping the directive host in a
   * `.gg-host-wrap.gg-loading` shell with its own `.gg-host-loader` div.
   * We promote that pattern down into the directive so every efile host
   * (Return Review steps, List of Overrides, Send Return to IRS, etc.)
   * gets the same opaque overlay automatically. The overlay is
   * positioned via `inset: 0` inside the host (no window math, no
   * offset calculations) and `pointer-events: none` so any user
   * interaction with the toolbar still goes through.
   *
   * Submission-data's wrapper-level overlay still wins visually because
   * `.gg-host-wrap.gg-loading > .gg-host { visibility: hidden }` also
   * hides our intra-host overlay — no double spinner.
   *
   * The legacy "Please use filters above to fetch data" placeholder
   * image (`base.elements.fliterOverlay`) is still suppressed via
   * `hideSelectFiltersOverlay` from `initGrid()` and
   * `processAndLoadData()` because every migrated efile screen ships
   * its own filter UI above the grid and that placeholder was
   * mis-aligned inside the host.
   */
  showLoader(): void {
    this.showLoaderOverlay();
    // Keep the native call as a defensive no-op — any host that still
    // calls `directive.api.showLoader()` directly behaves identically
    // to the previous revision.
    this.ggApi?.showLoader?.();
  }

  hideLoader(): void {
    this.hideLoaderOverlay();
    this.ggApi?.hideLoader?.();
  }

  // ---------------------------------------------------------------------------
  // Grid initialization
  // ---------------------------------------------------------------------------

  private initGrid(): void {
    if (this.destroyed || this.gridInitialized) {
      return;
    }
    if (typeof $ === 'undefined' || typeof $.fn?.gizmoGrid !== 'function') {
      console.warn('[EfileGrid] jQuery or gizmoGrid plugin not available');
      return;
    }

    const el = this.el.nativeElement;
    if (!el) return;

    const $grid = $(el);
    $grid.html(GRID_SCAFFOLD_HTML);

    $grid.gizmoGrid({
      fixedColumn: 2,
      gridHeight: 'auto',
      defaultRowHeight: 35,
      callBack: (obj: any) => this.onGridCallback(obj),
    });

    // Grid footer (filter chips + Total Records + Line Number) lives below
    // `.gg-body`. gizmoGrid `buildGrid()` sizes `.gg-body` from the window
    // budget and, for bounded hosts (submission `.gg-host-wrap`), reconciles
    // with `host.clientHeight` so the body fills the shell — avoiding white
    // space under the footer when viewport math is tighter than the host.

    this.ggApi = $grid.data('gizmoGridApi');
    this.gridInitialized = true;

    // -----------------------------------------------------------------
    // Vertical-scroll fix (parity with AdminGridDirective.initGrid).
    //
    // gizmoGrid binds `mousewheel DOMMouseScroll` directly on the grid
    // host (`gg.$el`) at gizmoGrid.js L10146. Every wheel event inside
    // the grid bubbles up to that host handler, which calls
    // `gg.scrollUp()` / `gg.scrollDown()` to scroll the grid body.
    //
    // That breaks scrolling inside column-header filter dropdowns
    // (`.gg-multi-select`): when the user wheels over the dropdown's
    // option list, the event bubbles to the host and the GRID scrolls
    // vertically instead of the dropdown's own list — making the
    // dropdown effectively unscrollable. From the user's perspective
    // this reads as "vertical scroll is not working properly" because
    // the visible scroll target (the open filter list) refuses to move.
    //
    // A delegated namespaced handler intercepts the wheel event at
    // `.gg-multi-select` and stops it bubbling, so the plugin's host
    // handler never fires and the dropdown's native scroll takes over.
    // The namespace lets `destroyGrid` cleanly unbind without touching
    // any handlers gizmoGrid itself attached to the host.
    // -----------------------------------------------------------------
    $grid.on(
      'mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect',
      '.gg-multi-select',
      (event: any) => {
        event.stopPropagation();
      },
    );
    $grid.on('click.filteredExportSync', '.tools-export', () => {
      this.syncFilteredExportVisibility();
    });

    $grid.on('click.exportAction', '.gg-export-action', (event: any) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const action = $(event.currentTarget).attr('action');
      if (this.gridConfig?.delegateExcelExport) {
        this.showExportSpinner();
        this.excelExportRequested.emit(action === 'filtered-export' ? 'filtered' : 'all');
        return;
      }
      if (action === 'filtered-export') {
        this.exportToExcelFiltered();
      } else {
        this.exportToExcel();
      }
    });

    this.syncFilteredExportVisibility();

    this.detachFloatingOverlays($grid);
    // Suppress the legacy "Please use filters above to fetch data"
    // placeholder image (`base.elements.fliterOverlay`) for every efile
    // host. Migrated efile screens (Submission List, List of Overrides,
    // Return Review, etc.) all ship their own filter UI above the grid
    // and rely on the gizmoGrid scaffold (toolbar + empty body + footer
    // with "Total Records: 0") for the empty state — same behaviour as
    // `AdminGridDirective`. Without this, the placeholder paints during
    // the gap between mount and first `loadData()` and shows up
    // mis-aligned inside the host (see List of Overrides screenshot).
    // It's also re-asserted from `processAndLoadData` so any later
    // gizmoGrid re-create cycle (refresh, sub-state switch) stays
    // covered.
    this.hideSelectFiltersOverlay($grid);
    // Scaffold has no row height until buildGrid() runs. An empty payload
    // makes loadGrid() return before that, and a deferred filter load never
    // calls it, so the horizontal scrollbar and footer stack into a thin bar.
    this.applyEmptyGridChrome();
    // Install the directive-managed loader overlay (translucent backdrop
    // + centred spinner) before any `showLoader()` call so the very first
    // `fetchAndLoad` paint already has a target to flip visible.
    this.installLoaderOverlay();
    this.bindInlineEditRealign($grid);
    // Bound the wrapper to the actual viewport space (kills the bottom
    // whitespace) and start observing for ancestor-driven width/height
    // changes so the horizontal-scroll math always reflects the live host.
    this.scheduleLayoutSync();
    this.observeHostResize();
    // Closes the blank column after the last field when this grid is
    // scrolled to the end. Scoped to this host; the shared grid is unchanged.
    this.observeEndGap();

    const autoLoad = this.gridConfig?.autoLoad !== false;
    if (autoLoad) {
      const force =
        this.gridConfig?.skipCacheOnInitialLoad === true;
      this.fetchAndLoad(undefined, force);
    } else {
      this.flushPendingPayload();
    }
  }

  /**
   * gizmoGrid.js appends floating overlays to the directive host (see
   * `ui/lib/gizmo-grid/gizmoGrid.js`). Some overlays are
   * `position: absolute` and gets its `top/left` set from `event.pageX` /
   * `event.pageY` (`positionContextMenu` L8350-L8369). The legacy
   * AngularJS layout had no positioned ancestor around `<div gg-grid>`,
   * so those page coordinates lined up with the document. Our migrated
   * shell wraps the host in `.gg-host-wrap` / `.gg-host` (both
   * `position: relative` — needed to anchor row-action popovers), which
   * turns the page coordinates into host-relative ones and shifts the
   * context menu off-screen.
   *
   * Re-parenting eligible overlays to `<body>` after `gizmoGrid()` runs gives
   * back a `position: absolute` resolved against the document, matching
   * legacy behaviour. We only move the floaters that are full-page
   * overlays — the context menu (`.gg-context-menu`) is intentionally
   * kept attached to the grid host because gizmoGrid wires its action clicks
   * with delegated handlers on `gg.$el` (e.g. `.on('click', '.gg-context-action', ...)`).
   * Moving the context menu out of `gg.$el` breaks those callbacks, so
   * context-menu items stop opening their modals/actions.
   *
   * The inline editor (`.edit-grid-value`) is also intentionally
   * left attached because it positions itself per-cell relative to the
   * host.
   */
  private detachFloatingOverlays($grid: any): void {
    if (typeof $ === 'undefined') return;
    const overlaySelectors = [
      '.gg-message-container',
      '.gg-error-container',
    ];
    for (const selector of overlaySelectors) {
      const $overlay = $grid.children(selector);
      if ($overlay.length) {
        $overlay.appendTo('body');
        // Once re-parented to <body>, right-clicks on the overlay itself fall
        // outside the directive host's bubbling tree, so the host-level
        // `@HostListener('contextmenu')` cannot intercept them. Attach the
        // same suppression directly so users never see the browser's native
        // menu stacked on top of gizmoGrid's overlays.
        $overlay.on('contextmenu', (e: any) => e.preventDefault());
        this.detachedOverlays.push($overlay);
      }
    }
  }

  private alignContextMenu(event: MouseEvent): void {
    if (typeof $ === 'undefined') return;
    const $grid = $(this.el.nativeElement);
    const $menu = $grid.children('.gg-context-menu');
    if (!$menu.length || !$menu.is(':visible')) return;

    const offsetParent = $menu.offsetParent()?.get(0) as HTMLElement | undefined;
    const parentRect = offsetParent
      ? offsetParent.getBoundingClientRect()
      : document.documentElement.getBoundingClientRect();
    const parentScrollLeft = offsetParent
      ? offsetParent.scrollLeft
      : window.pageXOffset;
    const parentScrollTop = offsetParent
      ? offsetParent.scrollTop
      : window.pageYOffset;
    const bodyRect = this.gridBodyRect();
    const menuWidth = $menu.outerWidth() || 0;
    const menuHeight = $menu.outerHeight() || 0;

    let left = event.clientX - parentRect.left + parentScrollLeft;
    let top = event.clientY - parentRect.top + parentScrollTop;

    if (bodyRect) {
      const minLeft = bodyRect.left - parentRect.left + parentScrollLeft;
      const minTop = bodyRect.top - parentRect.top + parentScrollTop;
      const maxLeft = bodyRect.right - parentRect.left + parentScrollLeft - menuWidth;
      const maxTop = bodyRect.bottom - parentRect.top + parentScrollTop - menuHeight;
      left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
      top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
    }

    $menu.css({
      position: 'absolute',
      left: `${Math.max(0, left)}px`,
      top: `${Math.max(0, top)}px`,
      zIndex: 10000,
    });
  }

  private gridBodyRect(): DOMRect | null {
    const host = this.el.nativeElement as HTMLElement;
    const body =
      host.querySelector('.gg-body') ||
      host.querySelector('.gg-grid-body') ||
      host.querySelector('.grid-body') ||
      host.querySelector('.gg-data-container');
    return (body as HTMLElement | null)?.getBoundingClientRect?.() || host.getBoundingClientRect();
  }

  // ---------------------------------------------------------------------------
  // Inline-edit positioning fix (see field-comment block above)
  // ---------------------------------------------------------------------------

  private bindInlineEditRealign($grid: any): void {
    const overlay = ($grid.children('.edit-grid-value').get(0) as HTMLElement | undefined)
      || ($grid.find('.edit-grid-value').get(0) as HTMLElement | undefined);
    if (!overlay) return;

    if (this.editorObserver) {
      this.editorObserver.disconnect();
    }

    this.editorObserver = new MutationObserver(() => {
      if (this.suppressEditorRealign) return;
      // The plugin sets `display: block` (via .show()) when opening and
      // `display: none` when closing. Skip realign when the editor is not
      // visible — top/left are stale leftovers in that case.
      const computedDisplay = window.getComputedStyle(overlay).display;
      if (computedDisplay === 'none') return;
      // Defer past the current frame so `textAreaEdit` finishes writing
      // `top`/`left` and any tab / flex layout (Material transforms) settles.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.suppressEditorRealign) return;
          if (window.getComputedStyle(overlay).display === 'none') return;
          this.realignInlineEditor(overlay);
        });
      });
    });

    this.editorObserver.observe(overlay, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  private realignInlineEditor(overlay: HTMLElement): void {
    const hostEl = this.el?.nativeElement as HTMLElement | null;
    if (!hostEl || !hostEl.contains(overlay)) return;

    const opEl = overlay.offsetParent as HTMLElement | null;
    if (!opEl) return;

    const objId = this.lastGridDataPayload?.selected?.obj_id;
    let cellEl: HTMLElement | null = null;
    if (objId != null && String(objId).trim() !== '') {
      const id = String(objId);
      try {
        const escaped =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(id)
            : id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        cellEl = hostEl.querySelector(`[obj_id="${escaped}"]`) as HTMLElement | null;
      } catch {
        cellEl = null;
      }
    }

    this.suppressEditorRealign = true;
    try {
      if (cellEl && hostEl.contains(cellEl)) {
        // Anchor to the actual selected cell — robust vs. transformed / scrolled
        // ancestors where jQuery `offsetParent` + document `offset()` math drifts.
        const cellRect = cellEl.getBoundingClientRect();
        const opRect = opEl.getBoundingClientRect();
        const top = cellRect.top - opRect.top + opEl.scrollTop;
        const left = cellRect.left - opRect.left + opEl.scrollLeft;
        overlay.style.top = `${Math.round(top)}px`;
        overlay.style.left = `${Math.round(left)}px`;
        const w = cellRect.width;
        if (w > 0) {
          overlay.style.width = `${Math.round(w)}px`;
        }
      } else if (typeof $ !== 'undefined') {
        // Fallback: jQuery offsetParent correction (original behaviour).
        const $overlay = $(overlay);
        const $offsetParent = $overlay.offsetParent();
        if (!$offsetParent?.length) return;

        const op = $offsetParent[0] as HTMLElement;
        const parentOff = $offsetParent.offset();
        if (!parentOff) return;

        const opDocTop = parentOff.top;
        const opDocLeft = parentOff.left;

        const rawTop = overlay.style.top;
        const rawLeft = overlay.style.left;
        const currentTop = parseFloat(rawTop) || 0;
        const currentLeft = parseFloat(rawLeft) || 0;

        if (!rawTop && !rawLeft && currentTop === 0 && currentLeft === 0) {
          return;
        }

        if (currentTop < opDocTop && currentLeft < opDocLeft) {
          return;
        }

        const correctedTop = currentTop - opDocTop + op.scrollTop;
        const correctedLeft = currentLeft - opDocLeft + op.scrollLeft;
        overlay.style.top = `${correctedTop}px`;
        overlay.style.left = `${correctedLeft}px`;
      }
    } finally {
      Promise.resolve().then(() => {
        this.suppressEditorRealign = false;
      });
    }

    const inputEl = overlay.querySelector('input,textarea') as HTMLElement | null;
    if (inputEl && document.activeElement !== inputEl) {
      try {
        inputEl.focus();
      } catch (_e) {
        /* ignore */
      }
    }
  }

  private destroyGrid(): void {
    if (this.loadSub) {
      this.loadSub.unsubscribe();
      this.loadSub = undefined;
    }
    // Drop our namespaced multi-select wheel guard before tearing the
    // plugin down. Unbinding by namespace leaves any handlers gizmoGrid
    // itself attached to the host alone.
    const $grid = $(this.el.nativeElement);
    if ($grid && $grid.length) {
      $grid.off('mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect');
      $grid.off('click.exportAction');
      $grid.off('click.filteredExportSync');
    }
    if (this.ggApi?.destroy) {
      this.ggApi.destroy();
      this.ggApi = null;
    }
    // Remove the floating overlays we re-parented to <body> in initGrid so
    // they don't leak across grid mount/unmount cycles (tab switches,
    // sub-state changes inside <submission-detail>, etc.).
    if (this.detachedOverlays.length) {
      for (const $overlay of this.detachedOverlays) {
        try { $overlay.remove(); } catch (_e) { /* ignore */ }
      }
      this.detachedOverlays = [];
    }
    if (this.editorObserver) {
      this.editorObserver.disconnect();
      this.editorObserver = undefined;
    }
    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect(); } catch (_e) { /* ignore */ }
      this.resizeObserver = undefined;
    }
    if (this.endGapObserver) {
      try { this.endGapObserver.disconnect(); } catch (_e) { /* ignore */ }
      this.endGapObserver = undefined;
    }
    if (this.endGapRaf) {
      cancelAnimationFrame(this.endGapRaf);
      this.endGapRaf = 0;
    }
    this.resizeRafScheduled = false;
    this.lastSyncedHostHeight = 0;
    this.lastGridDataPayload = null;
    this.gridInitialized = false;
    // Drop the `gg-empty` host marker so a future mount doesn't start out
    // with stale styling (e.g. scrollbars hidden before the next load
    // resolves). Pair with the `processAndLoadData` toggle.
    const hostEl = this.el?.nativeElement as HTMLElement | null;
    if (hostEl && hostEl.classList) {
      hostEl.classList.remove('gg-empty');
    }
    // Detach the directive-managed loader overlay so a remount (tab
    // switch, sub-state change) starts from a clean slate and `installLoaderOverlay()`
    // can write a fresh node. Also restore the host's `position` so we
    // don't leak our `position: relative` when the host is reused by
    // something other than another `[efileGrid]` mount.
    if (this.loaderOverlayEl) {
      try {
        this.loaderOverlayEl.parentElement?.removeChild(this.loaderOverlayEl);
      } catch (_e) {
        /* ignore — already detached by a parent re-render */
      }
      this.loaderOverlayEl = null;
    }
    if (hostEl && this.hostOriginalPosition !== null) {
      hostEl.style.position = this.hostOriginalPosition;
      this.hostOriginalPosition = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  private buildLoadRequestKey(url: string, params: Record<string, any>): string {
    const encodedParams = Object.keys(params)
      .sort()
      .filter((key) => params[key] !== null && params[key] !== undefined)
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`,
      )
      .join('&');
    return `${url}|${encodedParams}`;
  }

  /**
   * Identity for one grid read, shared across a host that is destroyed and
   * recreated while Return Review route state settles.
   *
   * The full request blob includes workspace fields (`screen_key`,
   * `activity_key`, …) that change between those two mounts. Keying the
   * in-flight share on that blob treated the same tab load as two requests
   * and posted `loadGridJson` twice. This key keeps the URL plus the host's
   * own params (`submission_id`, `form_id`, filter extras) and the stable
   * year/scenario/jcd values.
   */
  private buildCoalesceKey(
    url: string,
    extraParams: Record<string, any> | undefined,
    overrideParams: Record<string, any> | undefined,
  ): string {
    const globalParams = this.globalService.global?.globalParams || {};
    const stable: Record<string, any> = {};
    for (const key of ['tax_year', 'scenario', 'scenarioCode', 'jcd_key']) {
      const value = globalParams[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        stable[key] = value;
      }
    }
    return this.buildLoadRequestKey(url, {
      ...stable,
      ...(extraParams || {}),
      ...(overrideParams || {}),
    });
  }

  private fetchAndLoad(
    overrideParams?: Record<string, any>,
    forceRefresh = false,
    userRefresh = false,
  ): void {
    if (!this.ggApi || !this.gridConfig) return;

    const cfg = this.gridConfig;
    // Screens that declare filter criteria (Run Scrubbing, Scrubbing History)
    // stay on the empty scaffold until the user clicks Filter. An automatic
    // empty loadGridJson makes gizmoGrid raise the empty-data alert.
    if (this.deferLoadUntilUserFilter(cfg) && !userRefresh) {
      this.applyEmptyGridChrome();
      this.scheduleLayoutSync();
      return;
    }
    const cacheKey = cfg.cacheKey;

    if (!forceRefresh && cacheKey) {
      const cached = this.cacheService.get(cacheKey);
      if (cached) {
        cached.firstLoad = 0;
        this.processAndLoadData(cached);
        return;
      }
    }

    if (forceRefresh && userRefresh && cacheKey) {
      this.cacheService.clear(cacheKey);
    }

    if (this.loadSub) {
      this.loadSub.unsubscribe();
      this.loadSub = undefined;
    }

    const actionParam = cfg.actionCode
      ? `action_code=${cfg.actionCode}`
      : `action_id=${cfg.actionId}`;
    const url = `${environment.base_url}/loadGridJson?${actionParam}&grid_id=${cfg.gridId}`;
    const globalParams = this.globalService.global?.globalParams || {};
    // Custom-screen envelope — see `resolveCustomScreenContext()` above.
    // Layered between `globalParams` and `cfg.extraParams` so the host can
    // still override individual keys via `extraParams` while screens that
    // never explicitly populate them (most efile custom-grid hosts)
    // automatically pick up `activity_key`/`screen_key`/`jcd_ta_key` from
    // the workspace's active route. Same envelope is forwarded into any
    // CE modal launched from this grid via `prepareDialogData`, so the
    // add-edit `s3hr77`/`rv8nq0` calls receive them too.
    const customScreenCtx = this.resolveCustomScreenContext();
    const params = {
      ...globalParams,
      ...customScreenCtx,
      ...(cfg.extraParams || {}),
      ...(overrideParams || {}),
    };

    // Pure pass-through to gizmoGrid's loader — `showLoader()` is now
    // a thin wrapper around `ggApi.showLoader?.()`, exact parity with
    // `AdminGridDirective`. gizmoGrid sizes the overlay to
    // `width: $el.width() + 30, height: $(window).height() - $el.offset().top`
    // and centres the spinner via `.viewport-center`, so the loader is
    // visible from the first frame of the fetch through to `loadData()`
    // tearing it down — including filter clicks on gated Return Review
    // steps (XML Counts by Entity, XML vs Elements).
    this.showLoader();
    this.loadingStateChange.emit(true);

    // Share by grid identity, not the full param blob. `loadOnce` keeps the
    // HTTP alive after the first host unsubscribes (`refCount: false`), and
    // the tap records a successful payload even when that host is already
    // gone — otherwise the replacement host misses both the in-flight share
    // and the cache and posts `loadGridJson` again. A real refresh still
    // clears `cacheKey` above; once the in-flight read has finished,
    // `loadOnce` starts a new POST.
    const coalesceKey = this.buildCoalesceKey(url, cfg.extraParams, overrideParams);
    this.loadSub = this.cacheService
      .loadOnce(
        coalesceKey,
        () =>
          this.gridFactory.loadGridData(url, params).pipe(
            tap((data: any) => {
              if (data?.callSuccess === '1' && cacheKey) {
                this.cacheService.set(cacheKey, data);
              }
            }),
          ),
        userRefresh,
      )
      .pipe(take(1))
      .subscribe({
        next: (data: any) => {
          if (data?.callSuccess === '1') {
            this.processAndLoadData(data);
            if (cacheKey) {
              this.cacheService.set(cacheKey, data);
            }
          } else {
            console.warn('[EfileGrid] loadGridData failed:', data?.errorMessage);
            // Route through `hideLoader()` so the directive-managed
            // overlay clears alongside gizmoGrid's native loader on
            // payload-level failures (callSuccess !== '1').
            this.hideLoader();
          }
          this.loadingStateChange.emit(false);
        },
        error: (error: any) => {
          console.error('[EfileGrid] loadGridData error:', error);
          this.hideLoader();
          this.loadingStateChange.emit(false);
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Data processing (mirrors AdminGridDirective / GridDirective)
  // ---------------------------------------------------------------------------

  private processAndLoadData(data: any): void {
    if (!this.ggApi) return;

    if (data.settings) {
      try {
        data.settings.batch_select_rules = data.settings.batch_select_rules
          ? (typeof data.settings.batch_select_rules === 'string'
            ? JSON.parse(data.settings.batch_select_rules) : data.settings.batch_select_rules)
          : '';
      } catch (_e) {
        data.settings.batch_select_rules = '';
      }
    }

    // Snapshot the user's column filter chips / sort / global-search input
    // BEFORE the firstLoad reset below wipes them. Mirrors the equivalent
    // fix in the core workspace `GridDirective` (which had to do this via
    // a transient `_preservedTableStateOnReload` property because the
    // singleton `TabGridLoaderService.finalize` overwrites
    // `activeScreen.data` before `processAndLoadData` runs). Efile grids
    // use `this.lastGridDataPayload` as their data carrier and only
    // overwrite it at the END of `processAndLoadData` (~line 1054), so the
    // previous tableState is still intact at this point — no transient
    // property needed. Drill-down navigation does not exist for efile
    // grids, so the bypass branch from the core directive is intentionally
    // omitted.
    const isFreshFirstLoad = data.firstLoad == null || data.firstLoad === 1;
    let priorFilterState: {
      colSearch: any[];
      sort: any;
      globalValue: any;
    } | null = null;
    if (isFreshFirstLoad && this.lastGridDataPayload?.tableState) {
      const prev = this.lastGridDataPayload.tableState;
      const hasColSearch = Array.isArray(prev.colSearch) && prev.colSearch.length > 0;
      const hasSort = prev.sort && !_.isEmpty(prev.sort);
      const hasGlobalSearch =
        prev.search?.globalValue != null && prev.search.globalValue !== '';
      if (hasColSearch || hasSort || hasGlobalSearch) {
        priorFilterState = {
          colSearch: hasColSearch ? _.cloneDeep(prev.colSearch) : [],
          sort: hasSort ? _.cloneDeep(prev.sort) : {},
          globalValue: hasGlobalSearch ? prev.search.globalValue : null,
        };
      }
    }

    if (data.firstLoad == null || data.firstLoad === 1) {
      data.batchSelect = [];
      data.selected = {
        color: 'green', select_type: null, row_id: null, col_id: null,
        obj_id: null, data_type: null, edit_type: null, validation: null,
        action: null, format: null, value: null, oldValue: null,
        rowData: null, editable: false, element: null,
      };
      data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      data.cellData = {};
      data.vDragg = { draggerPositionY: 0, scrollPositionY: 0, startIndex: 0, endIndex: 0 };
      data.hDragg = { draggerPositionX: 0, scrollPositionX: 0, startIndex: 0, endIndex: 0, maxStartIndex: 0 };
    }
    if (data.firstLoad == null) {
      data.firstLoad = 1;
    }

    // Restore the user-applied filter chips / sort / global-search captured
    // before the reset block above. Without this restore, every refresh
    // (`refresh()` / `forceRefresh=true`) silently drops the footer chips
    // and resets sort order. Defensive guards mirror the core grid
    // directive:
    //   - `validPredicateSet` drops chips whose column is missing from
    //     the new payload (would otherwise produce a "0 rows" state).
    //   - Sort is only restored when its predicate column still exists.
    //   - `normalizeColSearchTypes` coerces filter values to the new
    //     payload's column types, avoiding strict-equality mismatches in
    //     gizmoGrid's pipe().
    // Runs BEFORE `normalizeGridTableState` so the latter only fills in
    // shape defaults for any gaps we left, not the chips we restored.
    if (priorFilterState) {
      if (!data.tableState) {
        data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      }
      const validPredicateSet = new Set(
        (data.header || []).map((h: any) => String(h?.map))
      );
      const survivingColSearch = priorFilterState.colSearch.filter(
        (f: any) => f?.predicate && validPredicateSet.has(String(f.predicate))
      );
      if (survivingColSearch.length) {
        data.tableState.colSearch = this.normalizeColSearchTypes(survivingColSearch, data);
      }
      if (
        !_.isEmpty(priorFilterState.sort) &&
        priorFilterState.sort.predicate &&
        validPredicateSet.has(String(priorFilterState.sort.predicate))
      ) {
        data.tableState.sort = priorFilterState.sort;
      }
      if (priorFilterState.globalValue != null && priorFilterState.globalValue !== '') {
        data.tableState.search = {
          ...(data.tableState.search || {}),
          globalValue: priorFilterState.globalValue,
        };
      }
    }

    // gizmoGrid `pipe()` / `buildFilterFunctions()` assume `tableState.colSearch`
    // is always an array; some `loadGridJson` payloads omit it or send null,
    // which throws inside `buildFilterFunctions` and aborts `pipe()` before
    // the footer ("Total Records" / filter chips) is painted — especially
    // after column filters when `colSearch` is populated then refreshed.
    this.normalizeGridTableState(data);

    // Match core `GridDirective.processAndLoadData`: on non-initial loads,
    // reset row filter flags so client-side filter state stays consistent
    // with the freshly merged dataset.
    if (data.firstLoad === 0 && Array.isArray(data.data) && data.data.length > 0) {
      for (const item of data.data) {
        if (item && typeof item === 'object') {
          item.isFiltered = 'N';
        }
      }
    }

    this.processTemplates(data);

    const ggData: any = {};
    ggData.headerArray = data.header || [];
    ggData.dataArr = data.data || [];
    ggData.settings = data.settings;
    ggData.tableState = data.tableState;
    ggData.cellData = data.cellData;
    ggData.selected = data.selected;
    ggData.vDragg = data.vDragg;
    ggData.hDragg = data.hDragg;
    ggData.batchSelect = data.batchSelect || [];
    ggData.columnTemplates = data.columnTemplates || [];
    ggData.actionTemplates = data.actionTemplates || [];
    ggData.pivotActionTemplates = [];
    ggData.firstLoad = data.firstLoad;
    ggData.pivotGroup = data.pivotGroup || {};
    ggData.messageObject = [];
    ggData.userAccessLevel = this.appService.USER_SETTINGS?.user?.system_access_level || 1;
    ggData.sso_id = this.appService.USER_SETTINGS?.user?.sso_id || '';
    // gizmoGrid `textAreaEdit` uses a broken branch when `fromModal === true`
    // (mixes `offset().top` with `offsetLeft` + `document.querySelector`, L7378–7380).
    // Efile grids are not modal shells — keep the consistent `$(cell).offset()` path.
    ggData.fromModal = false;

    for (let i = 0; i < ggData.headerArray.length; i++) {
      ggData.headerArray[i].object_id = i.toString();
    }

    this.ggApi.hideOverLay?.();
    this.lastGridDataPayload = ggData;
    this.clearEmptyBodySize();
    this.ggApi.loadData({ data: ggData });
    // gizmoGrid's `loadData()` fades out its own `.gg-loader` once the
    // data is rendered, but our directive-managed overlay is a separate
    // sibling div — flip it back to `display: none` here so the
    // translucent backdrop doesn't linger over already-painted rows.
    this.hideLoaderOverlay();

    // Toggle a host-level `gg-empty` marker so the global stylesheet can
    // suppress legacy gizmoGrid chrome (chunky `.gg-vScroll` / `.gg-hScroll`
    // tracks) when the grid has no rows. Without this an unfiltered grid
    // (e.g. the Run Scrubbing tab before the user picks a Form/Scrubbing
    // value) still renders dark gray scrollbar bars inside an otherwise
    // empty pane — they show as stray vertical / horizontal "lines". The
    // class is removed as soon as data lands so virtual scroll keeps
    // working with real data.
    const isEmpty = !ggData.dataArr || ggData.dataArr.length === 0;
    const hostEl = this.el?.nativeElement as HTMLElement | null;
    if (isEmpty) {
      this.hideLoader();
      this.ggApi?.gridRefreshPipe?.([]);
      // loadGrid() returns before buildGrid() when there are no rows, so the
      // body stays at a 60px minimum and the footer overlaps the scrollbar.
      this.applyEmptyGridChrome();
    } else if (hostEl) {
      hostEl.classList.remove('gg-empty');
    }

    this.lastLoadedData = data;
    this.gridDataLoaded.emit(data);
    // Defensive: re-assert the legacy "Please use filters above to fetch
    // data" placeholder image (`base.elements.fliterOverlay`) is hidden
    // once real rows are on screen. gizmoGrid's `loadData()` already
    // sets it to `display: none` (plain inline, no `!important`); this
    // re-pin with `!important` covers the edge case where a host
    // stylesheet ever forces the placeholder back on screen. The
    // spinner overlay (`base.elements.dataLoader`) does NOT need a
    // similar guard now that `showLoader()` is a pure pass-through to
    // gizmoGrid — `hideLoader()`'s `fadeOut` → plain `display: none`
    // is no longer fighting an `!important` from us.
    const $grid = $(this.el.nativeElement);
    this.hideSelectFiltersOverlay($grid);
    // Re-bound the host wrapper and rebuild gizmoGrid layout after the data
    // (and any newly-injected filter chips / footer text) settle. Without
    // this, `.gg-host-wrap`'s legacy `calc(100vh - 200px)` can leave a band
    // of white space below `.gg-footer`, and gizmoGrid's `buildHScrollBar`
    // can keep the stale horizontal-scroll window — making rightward scroll
    // paint blank trailing columns instead of `Zip` / `XML` / submission
    // date / status.
    this.scheduleLayoutSync();
  }

  /**
   * True when this grid has a filter bar and the load was not started by
   * the user (Filter button / `refresh()`). Other efile grids leave
   * `emptyAlertFilterParams` unset and keep auto-loading.
   */
  private deferLoadUntilUserFilter(cfg: EfileGridConfig): boolean {
    return !!cfg.emptyAlertFilterParams?.length;
  }

  /**
   * Paint a full empty grid: hide the chunky scrollbars, drop the scaffold's
   * placeholder filter chips, and give `.gg-body` the height `buildGrid()`
   * would have set. Without that height the 15px horizontal track and the
   * footer (filter icon, clear buttons, Line Number) stack under the toolbar
   * and the rest of the pane is blank.
   */
  private applyEmptyGridChrome(): void {
    const host = this.el?.nativeElement as HTMLElement | null;
    if (!host) {
      return;
    }
    host.classList.add('gg-empty');
    host.querySelector('.gg-filter-container')?.replaceChildren();
    // gizmoGrid's setFooterDetails() writes the count here, but loadGrid()
    // returns before it on an empty payload, leaving it blank or stale.
    const details = host.querySelector('.gg-footer-details .footer-details');
    if (details) {
      details.innerHTML = 'Total Records: <b>0</b>';
    }
    this.sizeEmptyGridBody(host);
  }

  private sizeEmptyGridBody(host: HTMLElement): void {
    const body = host.querySelector('.gg-body') as HTMLElement | null;
    if (!body) {
      return;
    }
    const footer = host.querySelector('.gg-footer') as HTMLElement | null;
    const chromeHeight = ['.gg-tools', '.gg-header'].reduce((sum, selector) => {
      const el = host.querySelector(selector) as HTMLElement | null;
      return sum + (el?.offsetHeight ?? 0);
    }, Math.max(footer?.offsetHeight ?? 0, 60));
    const available = host.clientHeight || host.parentElement?.clientHeight || 400;
    let height = Math.max(120, available - chromeHeight);
    body.style.height = `${height}px`;
    body.style.minHeight = '0';
    // `.gizmo-grid` is `overflow: hidden`. A body that is a few pixels too
    // tall pushes the footer — and the Line Number row — past the clip edge.
    const overflow = host.scrollHeight - host.clientHeight;
    if (overflow > 0) {
      height = Math.max(120, height - overflow);
      body.style.height = `${height}px`;
    }
  }

  /**
   * The app chrome footer (`.a-footer`) is `position: fixed; height: 55px`
   * and covers the bottom of the viewport. List of Overrides fills its pane,
   * and gizmoGrid then stretches `.gg-body` to that pane, so the grid footer
   * — Line Number — lands underneath the fixed bar. Cap the host above that
   * bar and keep 60px inside it for `.gg-footer`.
   */
  private fitOverridesFooter(): void {
    const host = this.el?.nativeElement as HTMLElement | null;
    if (!host?.classList.contains('overrides-grid-host')) {
      return;
    }
    const rect = host.getBoundingClientRect();
    if (!Number.isFinite(rect.top) || rect.top < 0) {
      return;
    }
    const available = Math.max(200, Math.floor(this.overridesVisibleBottom() - rect.top));
    if (Math.abs(rect.height - available) > 2) {
      host.style.flex = '0 0 auto';
      host.style.height = `${available}px`;
      host.style.maxHeight = `${available}px`;
    }
    const body = host.querySelector(':scope > .gg-body') as HTMLElement | null;
    if (!body) {
      return;
    }
    const footerHeight = 60;
    const hScrollHeight = host.classList.contains('gg-empty') ? 0 : 15;
    const tools = (host.querySelector(':scope > .gg-tools') as HTMLElement | null)?.offsetHeight ?? 0;
    const header = (host.querySelector(':scope > .gg-header') as HTMLElement | null)?.offsetHeight ?? 0;
    const height = Math.max(80, available - tools - header - hScrollHeight - footerHeight);
    body.style.height = `${height}px`;
    body.style.minHeight = '0px';
  }

  /** Lowest y still visible: the viewport, any clipping ancestor, and the fixed app footer. */
  private overridesVisibleBottom(): number {
    const host = this.el?.nativeElement as HTMLElement | null;
    let bottom = window.innerHeight;
    const appFooter = document.querySelector('.a-footer') as HTMLElement | null;
    if (appFooter) {
      const footerStyle = getComputedStyle(appFooter);
      if (footerStyle.display !== 'none' && (footerStyle.position === 'fixed' || footerStyle.position === 'sticky')) {
        const footerRect = appFooter.getBoundingClientRect();
        if (footerRect.height > 0) {
          bottom = Math.min(bottom, footerRect.top);
        }
      }
    }
    let node = host?.parentElement ?? null;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const clips = style.overflow === 'hidden' || style.overflowY === 'hidden'
        || style.overflowY === 'auto' || style.overflowY === 'scroll';
      if (clips) {
        bottom = Math.min(bottom, node.getBoundingClientRect().bottom);
      }
      node = node.parentElement;
    }
    return Math.max(0, bottom - 2);
  }

  private clearEmptyBodySize(): void {
    const body = this.el?.nativeElement?.querySelector('.gg-body') as HTMLElement | null;
    if (!body) {
      return;
    }
    body.style.height = '';
    body.style.minHeight = '';
  }

  /**
   * Force-hide the legacy `selectFiltersTemplate` overlay (the
   * `<img src="…arrow-filter-sm.png">` "Please use filters above to fetch
   * data" placeholder) once real grid data has rendered.
   *
   * gizmoGrid prepends this overlay alongside the spinner loader and
   * hides it via plain inline `display: none` from `loadData()`
   * (gizmoGrid.js L1310). That works on a vanilla mount, but if any
   * upstream code path ever sets `display: <truthy> !important` on it
   * (e.g. an earlier buggy `syncLoaderBounds` revision, or a host
   * stylesheet that targets `.gg-loader`), the plugin's plain inline
   * `display: none` cannot win against the `!important` and the
   * placeholder paints over already-loaded rows. Writing `display: none
   * !important` here from the directive guarantees the placeholder
   * disappears the moment data is on screen, regardless of upstream
   * specificity wars.
   */
  private hideSelectFiltersOverlay($grid: any): void {
    if (!$grid || !$grid.length) return;
    const $overlay = $grid.children('.gg-loader').filter(function (
      this: HTMLElement,
    ) {
      return $(this).find('img').length > 0;
    });
    if (!$overlay || !$overlay.length) return;
    $overlay.each(function (this: HTMLElement) {
      try {
        this.style.setProperty('display', 'none', 'important');
      } catch (_e) {
        /* ignore — read-only stylesheet edge cases */
      }
    });
  }

  /**
   * Build the directive-managed loader overlay and attach it inside the
   * grid host. Idempotent — safe to call from `initGrid()` regardless of
   * whether a previous mount left a stale node behind (we always wipe and
   * recreate). Visual chrome is matched to the rest of the workspace:
   *
   *   - translucent gray backdrop (`rgba(242, 242, 242, 0.85)`) so the
   *     in-flight grid still bleeds through faintly, mirroring gizmoGrid's
   *     native `.gg-loader { background: #f2f2f2 }` while keeping context.
   *   - centred AppKit spinner (`a-loading loading-large a-dark`), the
   *     same control gizmoGrid's `loadingTemplate` uses (gizmoGrid.js
   *     L8649) so we don't introduce a new visual vocabulary.
   *   - `pointer-events: none` so users can still hit gizmoGrid's
   *     toolbar (column color picker, etc.) if they manage to click
   *     during a fetch — matches admin-grid behaviour.
   *   - `z-index: 700` puts us above `.gg-loader` (`z-index: 640`) and
   *     gizmoGrid's column-action popovers, so a slow first frame from
   *     the native loader can't paint over our overlay.
   *
   * The host also gets `position: relative` (recorded into
   * `hostOriginalPosition` so we can restore on destroy) — `.gg-host`
   * already declares it via `submission-data.component.scss` but other
   * efile hosts (Return Review `.gizmo-grid`, scrubbing pane, etc.) only
   * have the default `position: static` and the overlay's `inset: 0`
   * would otherwise resolve against the wrong containing block.
   */
  private installLoaderOverlay(): void {
    const host = this.el?.nativeElement as HTMLElement | null;
    if (!host) return;

    if (this.loaderOverlayEl && this.loaderOverlayEl.parentElement === host) {
      try {
        host.removeChild(this.loaderOverlayEl);
      } catch (_e) {
        /* ignore */
      }
    }
    this.loaderOverlayEl = null;

    const computed = host.ownerDocument?.defaultView?.getComputedStyle?.(host);
    if (computed && computed.position === 'static') {
      this.hostOriginalPosition = host.style.position || '';
      host.style.position = 'relative';
    }

    const overlay = host.ownerDocument!.createElement('div');
    overlay.className = 'efile-grid-loader-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    // Small, always-visible loader: Font Awesome `fa-spinner fa-pulse`
    // (the same spinner every other migrated efile screen uses — see
    // `add-edit.component.html` form loader, `override-modal`,
    // `upload-files`, `return-review` loading template, etc.). The
    // previous AppKit `a-loading loading-large a-dark` span renders
    // nothing when the AppKit CSS bundle isn't loaded ahead of the
    // gizmoGrid scaffold, which is why the overlay used to look like a
    // bare gray panel. Font Awesome is bundled globally via
    // `styles.scss` so this spinner always paints. The accompanying
    // "Loading..." label gives the user explicit feedback that the
    // grid is still fetching, mirroring the workspace's
    // `.gt-grid-loading-overlay` text in `workspace.component.html`.
    //
    // The spinner is styled inline (size, color, gap) instead of via
    // a stylesheet rule because the overlay DOM is created at runtime
    // and lives outside any Angular component's encapsulated scope.
    overlay.innerHTML =
      '<div class="efile-grid-loader-overlay__spinner" ' +
      'style="display:flex;flex-direction:column;align-items:center;' +
      'justify-content:center;gap:10px;color:#555;">' +
      '<i class="fa fa-spinner fa-pulse" aria-hidden="true" ' +
      'style="font-size:32px;color:#e67e22;line-height:1;"></i>' +
      '<span style="font-size:13px;font-weight:500;">Loading...</span>' +
      '</div>';

    const style = overlay.style;
    style.position = 'absolute';
    style.top = '0';
    style.left = '0';
    style.right = '0';
    style.bottom = '0';
    style.display = 'none';
    style.alignItems = 'center';
    style.justifyContent = 'center';
    style.background = 'rgba(242, 242, 242, 0.85)';
    style.zIndex = '700';
    style.pointerEvents = 'none';

    host.appendChild(overlay);
    this.loaderOverlayEl = overlay;
  }

  /** Flip the directive-managed loader overlay into view. */
  private showLoaderOverlay(): void {
    const overlay = this.loaderOverlayEl;
    if (!overlay) return;
    overlay.style.display = 'flex';
  }

  /** Hide the directive-managed loader overlay. */
  private hideLoaderOverlay(): void {
    const overlay = this.loaderOverlayEl;
    if (!overlay) return;
    overlay.style.display = 'none';
  }

  /**
   * Resize the `.gg-host-wrap` shell to the actual viewport space below its
   * top edge. Inline style intentionally overrides the SCSS
   * `height: calc(100vh - 200px)` fallback so the wrapper height tracks the
   * real chrome above the grid (which varies with route, breadcrumb wrap,
   * filter-chip overflow, etc.) instead of a hard-coded constant.
   *
   * No-ops when the directive host isn't wrapped in `.gg-host-wrap` (other
   * efile hosts: scrubbing, overrides, return-review, etc.) so this stays
   * scoped to the Submission List custom grid shell.
   */
  private syncHostWrapperHeight(): void {
    const hostEl = this.el?.nativeElement as HTMLElement | null;
    if (!hostEl) return;
    const wrapperEl = hostEl.parentElement;
    if (!wrapperEl || !wrapperEl.classList.contains('gg-host-wrap')) return;

    const wrapperRect = wrapperEl.getBoundingClientRect();
    if (wrapperRect.top < 0 || !Number.isFinite(wrapperRect.top)) return;

    // Leave a small bottom safety pad so the host doesn't kiss the viewport
    // edge (browser scrollbars, OS chrome). 4px is enough to keep
    // gizmoGrid's `.gg-hScroll` clear without re-introducing a visible band.
    const safetyPad = 4;
    const available = Math.max(160, Math.floor(window.innerHeight - wrapperRect.top - safetyPad));
    if (Math.abs(this.lastSyncedHostHeight - available) < 2) return;

    this.lastSyncedHostHeight = available;
    wrapperEl.style.height = `${available}px`;
    wrapperEl.style.maxHeight = `${available}px`;
  }

  /**
   * The shared grid decides the furthest horizontal start index by walking
   * column widths until they pass the visible pane, then keeps that extra
   * step. At the right end the painted columns are therefore one short of
   * the pane, and the leftover width shows up as a blank column after the
   * last field (Submission Lock Status on the submissions grid).
   *
   * This runs only on the efile host. When the last field is already in
   * view and the tail is shorter than the pane, the previous column is
   * painted into that gap and the strip is shifted left by the overflow.
   * The clip stays on the left, so the last column remains fully visible
   * after a resize or zoom. Other grids never receive this adjustment.
   */
  private observeEndGap(): void {
    if (typeof MutationObserver === 'undefined') return;
    const host = this.el?.nativeElement as HTMLElement | null;
    if (!host) return;
    const headerRight = host.querySelector('.gg-header-right-col');
    const bodyRight = host.querySelector('.gg-right-col');
    if (!headerRight || !bodyRight) return;

    if (this.endGapObserver) {
      try { this.endGapObserver.disconnect(); } catch (_e) { /* ignore */ }
    }

    this.zone.runOutsideAngular(() => {
      this.endGapObserver = new MutationObserver((records) => {
        if (this.isOwnEndGapMutation(records)) return;
        this.scheduleEndGapAlign();
      });
      this.endGapObserver.observe(headerRight, { childList: true });
      this.endGapObserver.observe(bodyRight, { childList: true });
    });
  }

  private isOwnEndGapMutation(records: MutationRecord[]): boolean {
    if (!records.length) return false;
    for (const record of records) {
      const nodes = [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)];
      if (!nodes.length) return false;
      for (const node of nodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        if (!(node as Element).classList.contains('efile-end-fill')) return false;
      }
    }
    return true;
  }

  private scheduleEndGapAlign(): void {
    if (this.endGapRaf) return;
    this.endGapRaf = requestAnimationFrame(() => {
      this.endGapRaf = 0;
      this.alignEndGap();
    });
  }

  private alignEndGap(): void {
    const host = this.el?.nativeElement as HTMLElement | null;
    if (!host || !this.gridInitialized) return;
    const headerRight = host.querySelector('.gg-header-right-col') as HTMLElement | null;
    const bodyRight = host.querySelector('.gg-body .gg-right-col') as HTMLElement | null;
    if (!headerRight || !bodyRight) return;

    this.clearEndGapFill(headerRight, bodyRight);

    const nativeCols = Array.from(headerRight.children).filter(
      (node): node is HTMLElement =>
        node instanceof HTMLElement && node.classList.contains('headerCol'),
    );
    if (!nativeCols.length) return;

    const scrollable = this.scrollableHeaders();
    const lastHeader = scrollable[scrollable.length - 1];
    const lastMap = nativeCols[nativeCols.length - 1].getAttribute('map');
    if (!lastHeader || lastMap == null || String(lastHeader.map) !== String(lastMap)) {
      return;
    }

    const firstMap = nativeCols[0].getAttribute('map');
    let firstIdx = scrollable.findIndex((header) => String(header.map) === String(firstMap));
    if (firstIdx <= 0) return;

    const visibleWidth = this.rightPaneWidth(host, headerRight);
    if (!(visibleWidth > 0)) return;

    let rendered = nativeCols.reduce((sum, col) => sum + col.offsetWidth, 0);
    if (rendered + 1 >= visibleWidth) return;

    const fillers: any[] = [];
    let guard = scrollable.length;
    while (rendered + 1 < visibleWidth && firstIdx > 0 && guard-- > 0) {
      const prev = scrollable[firstIdx - 1];
      const width = this.headerWidthPx(prev);
      if (!(width > 0)) break;
      fillers.push(prev);
      rendered += width;
      firstIdx -= 1;
    }
    if (!fillers.length) return;

    const headerHeight = nativeCols[0].offsetHeight || 0;
    const rowsById = this.rowsByObjectId();

    for (const header of fillers) {
      const width = this.headerWidthPx(header);
      headerRight.insertBefore(this.buildFillerHeader(header, width, headerHeight), headerRight.firstChild);
      const rowNodes = Array.from(bodyRight.children).filter(
        (node): node is HTMLElement =>
          node instanceof HTMLElement && node.classList.contains('div-row'),
      );
      for (const row of rowNodes) {
        const rowId = row.getAttribute('rowObj');
        const data = rowId != null ? rowsById.get(String(rowId)) : undefined;
        row.insertBefore(this.buildFillerCell(header, width, data, rowId), row.firstChild);
      }
    }

    const painted = Array.from(headerRight.children).reduce((sum, node) => {
      return node instanceof HTMLElement && node.classList.contains('headerCol')
        ? sum + node.offsetWidth
        : sum;
    }, 0);
    const lastColWidth = nativeCols[nativeCols.length - 1].offsetWidth;
    const overflow = painted - visibleWidth;
    const maxShift = Math.max(0, painted - lastColWidth - 1);
    const shift = overflow > 1 && maxShift > 0 ? Math.min(Math.floor(overflow), maxShift) : 0;
    const margin = shift > 0 ? `-${shift}px` : '';
    headerRight.style.marginLeft = margin;
    bodyRight.style.marginLeft = margin;
  }

  private clearEndGapFill(headerRight: HTMLElement, bodyRight: HTMLElement): void {
    headerRight.querySelectorAll('.efile-end-fill').forEach((node) => node.remove());
    bodyRight.querySelectorAll('.efile-end-fill').forEach((node) => node.remove());
    headerRight.style.marginLeft = '';
    bodyRight.style.marginLeft = '';
  }

  private scrollableHeaders(): any[] {
    const headers = this.lastGridDataPayload?.headerArray;
    if (!Array.isArray(headers)) return [];
    const visible = headers.filter((header: any) => header && header.display === 'y');
    return visible.slice(Math.min(2, visible.length));
  }

  private headerWidthPx(header: any): number {
    const width = parseFloat(header?.width);
    return Number.isFinite(width) && width > 0 ? width : 100;
  }

  private rightPaneWidth(host: HTMLElement, headerRight: HTMLElement): number {
    const origin = parseFloat(headerRight.style.left);
    const hostRect = host.getBoundingClientRect();
    const vtrack = host.querySelector('.vScroll-track') as HTMLElement | null;
    const right = vtrack && vtrack.offsetWidth > 0
      ? vtrack.getBoundingClientRect().left
      : hostRect.right;
    const left = hostRect.left + (Number.isFinite(origin) ? origin : 0);
    return Math.round(right - left);
  }

  private rowsByObjectId(): Map<string, any> {
    const rows = new Map<string, any>();
    const data = this.lastGridDataPayload?.dataArr;
    if (!Array.isArray(data)) return rows;
    for (const row of data) {
      if (row && row.object_id != null) {
        rows.set(String(row.object_id), row);
      }
    }
    return rows;
  }

  private buildFillerHeader(header: any, width: number, height: number): HTMLElement {
    const col = document.createElement('div');
    col.className = 'headerCol efile-end-fill';
    if (header?.map != null) col.setAttribute('map', String(header.map));
    if (header?.object_id != null) col.setAttribute('colObj', String(header.object_id));
    col.style.width = `${width}px`;
    if (height > 0) col.style.height = `${height}px`;
    col.textContent = header?.label != null ? String(header.label) : '';
    return col;
  }

  private buildFillerCell(header: any, width: number, row: any, rowId: string | null): HTMLElement {
    const col = document.createElement('div');
    col.className = 'div-col efile-end-fill';
    const map = header?.map != null ? String(header.map) : '';
    if (map) col.setAttribute('colObj', map);
    if (rowId != null) col.setAttribute('rowObj', rowId);
    if (map && rowId != null) col.setAttribute('obj_id', `${rowId}-${map}`);
    col.style.width = `${width}px`;
    const data = document.createElement('div');
    data.className = 'div-col-data';
    const value = row && map ? row[map] : '';
    data.textContent = value == null || typeof value === 'object' ? '' : String(value);
    col.appendChild(data);
    return col;
  }

  /**
   * Ask gizmoGrid to recompute `.gg-body` height, `.gg-header` widths,
   * `.gg-hScroll` track / dragger geometry, and the right-column
   * virtualisation window (`hDragg.startIndex` / `endIndex`) for THIS grid
   * instance only.
   *
   * `base.api.gridRefresh()` is used on purpose — it is the one member
   * gizmoGrid actually attaches to the object exposed via
   * `$el.data('gizmoGridApi')` (`gg.api`) that re-runs the private
   * `buildGrid()` pass (gizmoGrid.js `gridRefresh: function(data){
   * buildGrid(); }`), scoped to the closure of this specific `base`/`gg`
   * instance. The core workspace `GridDirective` already relies on the same
   * call (`this.ggApi.gridRefresh()`) for the identical purpose.
   *
   * `base.resize()` (gizmoGrid.js `base.resize = function(e) {...}`) does
   * the same `buildGrid()` work, but it is assigned directly on the raw
   * plugin instance (`base`) — never merged into `base.api` — so
   * `this.ggApi.resize` is always `undefined` here and that branch never
   * actually ran; it silently fell through to `$(window).trigger('resize')`
   * on every settle pass (data load, filter, window resize, host
   * ResizeObserver tick).
   *
   * That fallback is a PAGE-WIDE broadcast: gizmoGrid binds its internal
   * resize handler per-instance on `$(window)`
   * (`$(window).resize(function(e){ gg.resize(e); })`), so triggering a
   * global `resize` event also re-runs `buildGrid()` on every OTHER
   * gizmoGrid still mounted elsewhere in the shell (other efile grids kept
   * alive off-screen via `visibility: hidden`, background routes, etc.). If
   * one of those happens to be width-0 at that moment, its own
   * `horizontalWidthArray` / `maxStartIndex` / `scrollWindowWidth` get
   * rebuilt against that bogus width — so when the user later returns to
   * THAT grid and scrolls right, trailing columns render blank. That is the
   * exact "intermittent blank columns while scrolling" failure mode
   * reported for the efile submissions dashboard grid, and it was only
   * intermittent because it depended on what else happened to be mounted
   * (and how it was sized) at the moment any grid on the page settled.
   *
   * Calling `gridRefresh()` keeps the recompute scoped to this directive's
   * own host, matching `GridDirective`'s pattern, and removes the
   * cross-instance side effect entirely. The global broadcast is kept only
   * as a last-resort fallback for the (currently unreachable) case where
   * `gridRefresh` itself is unavailable.
   */
  private triggerGridResize(): void {
    if (!this.gridInitialized || !this.ggApi) return;
    try {
      if (typeof this.ggApi.gridRefresh === 'function') {
        this.ggApi.gridRefresh();
        return;
      }
      if (typeof this.ggApi.resize === 'function') {
        this.ggApi.resize();
        return;
      }
    } catch (_e) {
      /* fall through to window dispatch as a last resort */
    }
    try {
      if (typeof $ !== 'undefined') {
        $(window).trigger('resize');
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Combined post-render layout pass. We schedule the same work over a few
   * frames because the chrome above the grid (workspace header, breadcrumb,
   * tab strip, filter row) can settle on different microtasks depending on
   * whether the route was deep-linked, switched via tab click, or restored
   * from the grid cache. Each pass is idempotent thanks to the `lastSyncedHostHeight`
   * guard inside `syncHostWrapperHeight`.
   */
  private scheduleLayoutSync(): void {
    const run = () => {
      this.syncHostWrapperHeight();
      this.triggerGridResize();
      const host = this.el?.nativeElement as HTMLElement | null;
      if (host?.classList.contains('gg-empty')) {
        this.sizeEmptyGridBody(host);
      }
      this.fitOverridesFooter();
    };
    setTimeout(run, 0);
    setTimeout(run, 80);
    setTimeout(run, 250);
  }

  /**
   * Watch the host wrapper for size changes (sidebar collapse, tab body
   * mount, font reflow) and ask gizmoGrid to re-layout outside Angular's
   * change detection. Coalesced into a single rAF per burst so a chain of
   * sub-pixel resizes doesn't thrash `buildGrid()`.
   */
  private observeHostResize(): void {
    if (typeof ResizeObserver === 'undefined') return;
    const hostEl = this.el?.nativeElement as HTMLElement | null;
    if (!hostEl) return;
    const wrapperEl = hostEl.parentElement;
    const target = (
      wrapperEl && wrapperEl.classList && wrapperEl.classList.contains('gg-host-wrap')
        ? wrapperEl
        : hostEl
    ) as HTMLElement;
    if (!target) return;

    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect(); } catch (_e) { /* ignore */ }
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.gridInitialized || this.resizeRafScheduled) return;
      this.resizeRafScheduled = true;
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          this.resizeRafScheduled = false;
          this.syncHostWrapperHeight();
          this.triggerGridResize();
          this.fitOverridesFooter();
        });
      });
    });
    try {
      this.resizeObserver.observe(target);
    } catch (_e) {
      /* ignore — ResizeObserver may throw on detached nodes */
    }
  }

  /**
   * Coerce colSearch `value[]` entries to the data type of the new payload's
   * actual row data. The backend is occasionally inconsistent — e.g. a
   * column may arrive as string in one response and as number in the next
   * after columns are recomputed. gizmoGrid's `pipe()` compares with strict
   * equality (`_.findByValues`), so a type mismatch silently filters the
   * grid down to zero rows. Mirrors `GridDirective.normalizeColSearchTypes`.
   */
  private normalizeColSearchTypes(filters: any[], data: any): any[] {
    const sampleRow = data?.data?.[0];
    const cloned = _.cloneDeep(filters);
    if (!sampleRow) return cloned;
    return cloned.map((f: any) => {
      if (!f || !Array.isArray(f.value) || f.value.length === 0) return f;
      const refVal = sampleRow[f.predicate];
      if (refVal == null) return f;
      const targetType = typeof refVal;
      if (targetType !== 'number' && targetType !== 'string') return f;
      const needsCoerce = f.value.some((v: any) => typeof v !== targetType);
      if (!needsCoerce) return f;
      f.value = f.value.map((v: any) => {
        if (targetType === 'number') {
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        }
        return String(v);
      });
      return f;
    });
  }

  /**
   * Ensure `data.tableState` always has the shape gizmoGrid expects so
   * `pipe()` → `buildFilterFunctions()` → `setFooterDetails()` completes
   * for every efile grid (submission list, scrubbing, overrides, etc.).
   */
  private normalizeGridTableState(data: any): void {
    const defaults = {
      sort: {} as Record<string, unknown>,
      search: {} as Record<string, unknown>,
      colSearch: [] as unknown[],
      pagination: { start: 0 },
    };
    if (!data || typeof data !== 'object') {
      return;
    }
    if (!data.tableState || typeof data.tableState !== 'object') {
      data.tableState = { ...defaults };
      return;
    }
    const ts = data.tableState as Record<string, unknown>;
    if (!Array.isArray(ts.colSearch)) {
      ts.colSearch = [...defaults.colSearch];
    }
    if (!ts.search || typeof ts.search !== 'object') {
      ts.search = { ...defaults.search };
    }
    if (!ts.sort || typeof ts.sort !== 'object') {
      ts.sort = { ...defaults.sort };
    }
    if (!ts.pagination || typeof ts.pagination !== 'object') {
      ts.pagination = { ...defaults.pagination };
    }
  }

  private processTemplates(data: any): void {
    if (!data.header) return;

    data.header.forEach((col: any) => {
      if (col.edit_rules && typeof col.edit_rules === 'string') {
        try { col.edit_rules = JSON.parse(col.edit_rules); } catch (_e) { /* ignore */ }
      }
    });

    if (data.columnTemplates) {
      data.columnTemplates.forEach((tpl: any) => {
        if (typeof tpl.columns === 'string') {
          try { tpl.columns = JSON.parse(tpl.columns); } catch (_e) { tpl.columns = ''; }
        }
      });
    } else {
      data.columnTemplates = [];
    }

    if (data.header?.length) {
      const templateArr = data.header.map((h: any) => ({
        width: h.width, display: h.display, map: h.map,
      }));
      const defaultTemplate = {
        name: 'Default Columns', template_key: 0, system: 1,
        default_template: 0, columns: templateArr,
      };
      if (!data.columnTemplates.some((t: any) => t.template_key === 0)) {
        data.columnTemplates.unshift(defaultTemplate);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Callback routing
  // ---------------------------------------------------------------------------

  private onGridCallback(object: any): void {
    const event: EfileGridCallbackEvent = {
      callName: object.callName,
      data: object.data,
      type: object.type,
    };

    this.zone.run(() => {
      if (object.type === 'columnTemplate') {
        this.gridSaveFactory.colTemplatesUpdate(
          this.gridConfig.gridId,
          object.callName,
          object.data,
        );
      }

      if (object.type === 'input' && object.callName) {
        const globalParams = this.globalService.global?.globalParams || {};
        this.gridSaveFactory.saveSwitch(object.callName, object.data, globalParams);
      }

      // Opt-in: route legacy CE-based modal callbacks through the shared
      // `CeHostDialogComponent` pipeline (mirrors core workspace
      // `GridDirective.setupCallbacks` switch). Hosts still receive
      // `gridCallback` for observability / refresh hooks.
      if (this.gridConfig?.autoOpenCeDialog) {
        switch (object.type) {
          case 'form':
            this.handleFormCallback(object);
            break;
          case 'modal':
            this.openLegacyModal(object.data);
            break;
          case 'openGridModal':
            this.handleOpenGridModal(object.callName, object.data);
            break;
          case 'comment':
          case 'attachment':
          case 'preview':
          case 'auditcell':
            this.handleModalByFactory(object.callName, object.data);
            break;
          default:
            break;
        }
      }

      this.gridCallback.emit(event);
    });
  }

  // ---------------------------------------------------------------------------
  // CE-host-dialog flow (parity with core `GridDirective.handleFormCallback`)
  // ---------------------------------------------------------------------------

  /**
   * gizmoGrid emits `type: 'form'` for pencil/inline-action buttons and for
   * batch toolbar actions. Resolve the CE registry first (fast path); fall
   * back to `/getScreenModal` so legacy modals still work when a registry
   * entry has not been added.
   */
  private handleFormCallback(object: any): void {
    const callName = object?.callName;
    const _data = object?.data;
    const colCallBack = _data?.colData?.call_back;

    const registryEntry = this.componentRegistry.resolve(callName);
    const registryByCol = colCallBack ? this.componentRegistry.resolve(colCallBack) : undefined;
    const chosenEntry = registryByCol || registryEntry;

    if (chosenEntry) {
      this.openCeDialog(chosenEntry.ceTagName, _data, chosenEntry, colCallBack || callName);
      return;
    }

    const modalName = colCallBack || callName;
    if (!modalName) {
      console.warn('[EfileGrid] Unhandled form callback:', callName);
      return;
    }

    this.modalFactory.getModal(modalName, _data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        if (!response || response.callSuccess !== '1') {
          this.showModalOpenError(response?.errorMessage);
          return;
        }

        const serverCeTag: string | undefined = response?.modalScreen?.ce_tag_name;
        const responseModalName: string | undefined = response?.modalScreen?.modal_name;
        const resolvedEntry =
          this.componentRegistry.resolve(colCallBack || '') ||
          this.componentRegistry.resolve(callName) ||
          (responseModalName ? this.componentRegistry.resolve(responseModalName) : undefined) ||
          (serverCeTag ? this.componentRegistry.resolve(serverCeTag) : undefined);
        const chosenTag = serverCeTag || resolvedEntry?.ceTagName;

        if (!chosenTag) {
          console.warn('[EfileGrid] No CE tag resolved for modal:', modalName, response?.modalScreen);
          this.showModalOpenError();
          return;
        }

        const lockPayload = this.buildLockPayload(response);
        const dialogWidth = this.resolveDialogWidth(
          response?.modalScreen?.modal_size,
          resolvedEntry?.dialogWidth,
        );
        const modalDesc = response?.modalScreen?.modal_desc;
        this.openCeDialog(chosenTag, _data, resolvedEntry, modalName, lockPayload, dialogWidth, modalDesc);
      },
      error: (err: any) => {
        console.error('[EfileGrid] getScreenModal error:', modalName, err);
        this.showModalOpenError();
      },
    });
  }

  private handleOpenGridModal(callName: string, _data: any): void {
    const registryEntry = this.componentRegistry.resolve(callName);
    if (registryEntry) {
      this.openCeDialog(registryEntry.ceTagName, _data, registryEntry, callName);
      return;
    }

    this.modalFactory.getModal(callName, _data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        if (!response?.modalScreen) return;
        const ceTag = response.modalScreen.ce_tag_name;
        const fallbackEntry = this.componentRegistry.resolve(callName);
        if (ceTag) {
          this.openCeDialog(ceTag, _data, fallbackEntry, callName);
        } else if (fallbackEntry) {
          this.openCeDialog(fallbackEntry.ceTagName, _data, fallbackEntry, callName);
        } else {
          console.warn('[EfileGrid] No CE tag resolved for openGridModal:', callName);
          this.showModalOpenError();
        }
      },
      error: (err: any) => console.error('[EfileGrid] openGridModal error:', callName, err),
    });
  }

  private handleModalByFactory(callName: string, _data: any): void {
    const registryEntry = this.componentRegistry.resolve(callName);
    if (registryEntry) {
      this.openCeDialog(registryEntry.ceTagName, _data, registryEntry, callName);
      return;
    }

    this.modalFactory.getModal(callName, _data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        if (!response?.modalScreen) return;
        const ceTag = response.modalScreen.ce_tag_name;
        const fallbackEntry = this.componentRegistry.resolve(callName);
        if (ceTag) {
          this.openCeDialog(ceTag, _data, fallbackEntry, callName);
        } else if (fallbackEntry) {
          this.openCeDialog(fallbackEntry.ceTagName, _data, fallbackEntry, callName);
        } else {
          console.warn('[EfileGrid] No CE tag resolved for modal:', callName);
          this.showModalOpenError();
        }
      },
      error: (err: any) => console.error('[EfileGrid] modalByFactory error:', callName, err),
    });
  }

  /**
   * Legacy `type: 'modal'` path — gizmoGrid passes `colData.modal_name`
   * directly. Mirrors `GridDirective.openModal` but routes through
   * `CeHostDialogComponent` instead of the dynamic `response.component`
   * injection used for legacy AngularJS templates.
   */
  private openLegacyModal(_data: any): void {
    const modalName = _data?.colData?.modal_name;
    if (!modalName) return;
    this.handleModalByFactory(modalName, _data);
  }

  private openCeDialog(
    ceTagName: string,
    _data: any,
    registryEntry?: any,
    callName?: string,
    lockPayload?: ModalLockPayload,
    widthOverride?: string,
    modalDesc?: string,
  ): void {
    const dialogData = this.prepareDialogData(
      ceTagName,
      _data,
      callName,
      lockPayload,
      modalDesc,
      registryEntry?.templateMode,
    );

    const basePanel = registryEntry?.dialogPanelClass ?? 'ce-host-dialog-panel';
    const rawPanels: string[] = Array.isArray(basePanel) ? [...basePanel] : [String(basePanel)];
    const panelClass: string[] = [];
    for (const token of rawPanels) {
      for (const part of String(token).trim().split(/\s+/)) {
        if (part) {
          panelClass.push(part);
        }
      }
    }
    if (lockPayload?.isLocked) {
      panelClass.push('modal-window-lock-class');
    }

    const isEfileCommentsModal =
      typeof ceTagName === 'string' && ceTagName.toLowerCase().includes('efile-comments-modal');
    if (isEfileCommentsModal) {
      panelClass.push('ce-host-dialog-efile-comments');
    }

    const dialogRef = this.dialog.open(CeHostDialogComponent, {
      data: dialogData,
      width: widthOverride ?? registryEntry?.dialogWidth ?? '90vw',
      height: isEfileCommentsModal ? undefined : registryEntry?.dialogHeight ?? undefined,
      panelClass,
      disableClose: true,
      ...(isEfileCommentsModal ? { position: { top: '48px' } } : {}),
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.action === 'refresh') {
        this.refresh();
      }
    });
  }

  /**
   * Build the `CeHostDialogData` payload for an efile screen. Delegated
   * to {@link buildEfileCeHostDialogData} so the directive,
   * `efile-ctrlfn-dispatcher`, and `XmlVsElementsComponent` all share the
   * same filterParams + headerData rules — including the per-callName
   * deriver table (today: `xml2ElementsCompare` injects `irs_id` from the
   * clicked row when the active tab filter is empty).
   *
   * `lastLoadedData?.header` (last successful `loadGridJson`) is passed
   * as the `headerLabel` fallback so modal-form CEs (e.g.
   * `gtw-efile-edit-submission-table-data-ce`) still build their layout
   * from the originating grid's columns when the active screen has no
   * `data.header` populated.
   */
  private prepareDialogData(
    ceTagName: string,
    _data: any,
    callName?: string,
    lockPayload?: ModalLockPayload,
    modalDesc?: string,
    templateMode?: string,
  ): CeHostDialogData {
    // Fold the custom-screen envelope (activity_key / screen_key /
    // jcd_ta_key / screen_type / jcd_key) into `gridExtraParams` so the
    // launched CE modal sees them via its `filter-params` input. Used by
    // the migrated add-edit modal (`s3hr77`/`rv8nq0`) and override-modal
    // (`rv8nq0`) — they can then build their own request payload from
    // `filterParams.<key>` without re-deriving from `globalService` or
    // `workspaceFactory.activeScreen`.
    //
    // Order: workspace/active-screen-derived keys first, host
    // `extraParams` last so the host can still override per-grid (e.g.
    // pinning `screen_type='custom'` when the activeScreen doesn't carry it).
    const customScreenCtx = this.resolveCustomScreenContext();
    const mergedExtraParams = {
      ...customScreenCtx,
      ...(this.gridConfig?.extraParams || {}),
    };

    return buildEfileCeHostDialogData({
      ceTagName,
      callName: callName || '',
      gridPayload: _data,
      workspaceService: this.workspaceService,
      globalService: this.globalService,
      appService: this.appService,
      gridExtraParams: mergedExtraParams,
      fallbackHeader: this.lastLoadedData?.header ?? null,
      modalDesc,
      lock: lockPayload,
      templateMode,
    });
  }

  /**
   * Translate the legacy `/getScreenModal` lock contract into an
   * Angular-friendly payload. Mirrors `GridDirective.buildLockPayload`.
   */
  private buildLockPayload(response: any): ModalLockPayload | undefined {
    const checkRequired = response?.modalScreen?.lock_check_required === 'Y';
    const isLocked = response?.lockResult?.is_locked === 'Y';
    if (!checkRequired || !isLocked) {
      return undefined;
    }
    return {
      isLocked: true,
      checkRequired: true,
      message: response?.lockResult?.lock_message || '',
    };
  }

  /**
   * Map legacy `modalScreen.modal_size` to a CSS width accepted by
   * MatDialog. Mirrors `GridDirective.resolveDialogWidth` so backend
   * `modal_size` strings (`sm` / `md` / `lg` / `xl`) keep working.
   */
  private resolveDialogWidth(modalSize?: string, fallback?: string): string {
    const fb = fallback ?? '90vw';
    if (!modalSize) return fb;
    const s = String(modalSize).trim().toLowerCase();
    switch (s) {
      case 'sm': return '350px';
      case 'md': return '70vw';
      case 'lg': return '92vw';
      case 'xl': return '96vw';
    }
    if (/^\d+(\.\d+)?(px|%|vw|vh|em|rem)$/.test(s)) return s;
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    return fb;
  }

  // ---------------------------------------------------------------------------
  // Export to Excel — Download All Data (parity with core GridDirective)
  // ---------------------------------------------------------------------------

  private showExportSpinner(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid || !$grid.length) return;
    const toolsExport = $grid.find('.tools-export');
    toolsExport.addClass('gg-export-to-excel');
    if (!toolsExport.find('i.fa-spinner').length) {
      toolsExport.append('<i class="fa fa-spinner fa-spin"></i>');
    }
  }

  private hideExportSpinner(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid || !$grid.length) return;
    const toolsExport = $grid.find('.tools-export');
    toolsExport.removeClass('gg-export-to-excel');
    toolsExport.find('i.fa-spinner').remove();
  }

  /** Legacy efile custom grids (hidden `#action_id` / `#screen_key` spans). */
  private isLegacyCustomGridExport(): boolean {
    const id = this.gridConfig?.exportActionId;
    const hasExportId = id !== undefined && id !== null && String(id).trim() !== '';
    return hasExportId && !this.gridConfig?.legacyGridTplExport;
  }

  /** Legacy `grid-tpl.html` / `EfileCustomGrid` export (return-review, scrubbing). */
  private isLegacyGridTplExport(): boolean {
    return this.gridConfig?.legacyGridTplExport === true;
  }

  /** Keys from `extraParams` that must not appear in grid-tpl export jsonObj. */
  private static readonly GRID_TPL_EXPORT_SKIP_KEYS = new Set([
    'submission_id',
    'SUBMISSION_ID',
    'scenario_id',
    'SCENARIO_ID',
    'sso_id',
    'SSO_ID',
    'tax_year',
    'type',
    'display_mE_code',
    'display_scenario_description',
    'display_filing_group_description',
    'display_filingGrp',
    'filing_key',
  ]);

  /**
   * Mirrors legacy `EfileCustomGrid` `all_filter_data` + display hidden spans.
   * Example (scrubbings / return-review):
   *   {"submission_id":"23335","scenario_id":"74",
   *    "display_mE_code":"true","display_scenario_description":"true",
   *    "display_filing_group_description":"1"}
   */
  private buildGridTplExportJsonData(extraColFilters?: Record<string, string>): Record<string, string> {
    const extra = this.gridConfig?.extraParams || {};
    const defaults = this.gridConfig?.exportDisplayDefaults;

    const jsonData: Record<string, string> = {
      submission_id: String(extra.submission_id ?? extra.SUBMISSION_ID ?? ''),
      scenario_id: String(extra.scenario_id ?? extra.SCENARIO_ID ?? ''),
      display_mE_code: defaults?.display_mE_code ?? 'true',
      display_scenario_description: defaults?.display_scenario_description ?? 'true',
      display_filing_group_description:
        defaults?.display_filing_group_description
        ?? String(extra.display_filingGrp ?? extra.filing_key ?? '1'),
    };

    Object.keys(extra).forEach((key) => {
      if (EfileGridDirective.GRID_TPL_EXPORT_SKIP_KEYS.has(key)) {
        return;
      }
      const v = extra[key];
      if (v != null && String(v).trim() !== '' && String(v) !== 'undefined' && String(v) !== 'null') {
        jsonData[key] = String(v);
      }
    });

    if (extraColFilters) {
      Object.keys(extraColFilters).forEach((key) => {
        jsonData[key] = extraColFilters[key];
      });
    }

    return jsonData;
  }

  /**
   * Mirrors gizmoGrid `exportExcel()` jsonObj for efile custom-grid templates:
   * submission_id + form_id from `extraParams`, static display_* flags from
   * hidden spans. Legacy validation/errors export must contain ONLY:
   *   submission_id, form_id, display_mE_code, display_scenario_description,
   *   display_filing_group_description
   * (plus optional colFilter_* keys for filtered export).
   */
  private buildLegacyCustomExportJsonData(extraColFilters?: Record<string, string>): Record<string, string> {
    const extra = this.gridConfig?.extraParams || {};
    const defaults = this.gridConfig?.exportDisplayDefaults;

    const submissionId = extra.submission_id ?? extra.SUBMISSION_ID;
    const formId = extra.form_id ?? extra.FORM_ID;

    // Fixed key order matches legacy gizmoGrid filterArrayDisplay parsing.
    const jsonData: Record<string, string> = {
      submission_id: submissionId != null ? String(submissionId) : '',
      form_id: formId != null ? String(formId) : '',
      display_mE_code: defaults?.display_mE_code ?? 'true',
      display_scenario_description: defaults?.display_scenario_description ?? 'true',
      display_filing_group_description:
        defaults?.display_filing_group_description
        ?? String(extra.display_filingGrp ?? extra.filing_key ?? '1'),
    };

    if (extraColFilters) {
      Object.keys(extraColFilters).forEach((key) => {
        jsonData[key] = extraColFilters[key];
      });
    }

    return jsonData;
  }

  private buildExportParams(): {
    jsonData: any;
    actionId: string;
    actionCode: string;
    gridId: string;
    ssoId: string;
    screenKey: string;
    activeScreen: any;
  } | null {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    const hasExportScreenKey =
      this.gridConfig?.exportScreenKey !== undefined
      && this.gridConfig?.exportScreenKey !== null
      && String(this.gridConfig.exportScreenKey).trim() !== '';
    const hasLegacyExportScreen =
      hasExportScreenKey || this.gridConfig?.legacyGridTplExport === true;
    if (!activeScreen?.screen_key && !hasLegacyExportScreen) return null;

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    // --- Resolve gridId ---
    let gridId = String(this.gridConfig?.gridId || '').trim();
    if (!gridId) {
      const settings = this.lastLoadedData?.settings || activeScreen?.data?.settings || {};
      const settingsGridId = settings.grid_id ?? settings.gridId;
      if (settingsGridId !== undefined && settingsGridId !== null && String(settingsGridId).trim() !== '') {
        gridId = String(settingsGridId).trim();
      }
    }

    // Legacy efile custom grids — separate load vs export identifiers and a
    // minimal jsonObj (NOT workspace globalParams).
    if (this.isLegacyCustomGridExport()) {
      const actionId = String(this.gridConfig!.exportActionId);
      const screenKey = String(this.gridConfig!.exportScreenKey ?? '');
      const exportGridId = String(
        this.gridConfig!.exportGridId ?? this.gridConfig!.gridId ?? gridId,
      ).trim();
      const jsonData = this.buildLegacyCustomExportJsonData();
      return {
        jsonData,
        actionId,
        actionCode: '',
        gridId: exportGridId || gridId,
        ssoId,
        screenKey,
        activeScreen,
      };
    }

    // Legacy grid-tpl (`EfileCustomGrid`) — same action_id as load (or
    // `exportActionId`), Tax Payer screen_key, all_filter_data jsonObj.
    if (this.isLegacyGridTplExport()) {
      const cfg = this.gridConfig!;
      const exportActionRef = cfg.exportActionId ?? cfg.actionId;
      const actionId =
        exportActionRef !== undefined && exportActionRef !== null
          ? String(exportActionRef)
          : '';
      const screenKey = String(cfg.exportScreenKey ?? '11212');
      const exportGridId = String(cfg.exportGridId ?? cfg.gridId ?? gridId).trim();
      const jsonData = this.buildGridTplExportJsonData();
      return {
        jsonData,
        actionId,
        actionCode: '',
        gridId: exportGridId || gridId,
        ssoId,
        screenKey,
        activeScreen,
      };
    }

    // Workspace-style efile grids (submissions list, overrides, …).
    let actionId = '';
    const cfgActionId = this.gridConfig?.actionId;
    if (cfgActionId !== undefined && cfgActionId !== null && String(cfgActionId).trim() !== '') {
      actionId = String(cfgActionId);
    }
    if (!actionId) {
      const settings = this.lastLoadedData?.settings || activeScreen?.data?.settings || {};
      const fallback = settings.action_id ?? settings.action_key ?? settings.actionId;
      if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
        actionId = String(fallback);
      }
    }
    const actionCode = actionId ? '' : String(this.gridConfig?.actionCode || '').trim();

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};
    const globalParams = this.globalService.global?.globalParams || {};
    const customScreenCtx = this.resolveCustomScreenContext();
    const merged = {
      ...globalParams,
      ...customScreenCtx,
      ...(this.gridConfig?.extraParams || {}),
      ...(filterParams || {}),
    };

    const jsonData: any = { isFileDownloadRequest: 'Y' };
    Object.keys(merged).forEach((k: string) => {
      const v = merged[k];
      if (v != null && v !== '' && v !== 'undefined' && v !== 'null') {
        jsonData[k] = String(v);
      }
    });

    const keysToExcludeDisplay = [
      'tax_year',
      'screen_key',
      'jcd_key',
      'jcd_ta_key',
      'activity_key',
      'screen_type',
      'is_locked',
      'isFileDownloadRequest',
    ];
    Object.keys(jsonData).forEach((k: string) => {
      if (!keysToExcludeDisplay.includes(k) && !k.startsWith('display_')) {
        jsonData[`display_${k}`] = jsonData[k];
      }
    });

    const screenKey = String(jsonData.screen_key ?? activeScreen.screen_key ?? '');
    return { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen };
  }

  private handleExportResponse(data: any, ssoId: string): void {
    const baseUrl = environment.base_url;
    if (data?.ERROR) {
      this.gtwSnackbarService.openSnackBar(data.ERROR, 'gtw-snackbar--red');
      this.exportDone();
      return;
    }
    if (data?.callSuccess != 1 && data?.callSuccess !== '1') {
      this.gtwSnackbarService.openSnackBar(
        'Data can not be exported, please contact System administrator',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    this.http.get<any>(`${baseUrl}/getAPIAuthToken`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tokenRes: any) => {
        if (tokenRes?.apiToken) {
          const clientKey = sessionStorage.getItem('client_key') || '';
          const downloadUrl = `${baseUrl}/download?file_name=${encodeURIComponent(data.file_name)}`
            + `&client_key=${encodeURIComponent(clientKey)}`
            + `&apiToken=${encodeURIComponent(tokenRes.apiToken)}`
            + `&georaclehrid=${encodeURIComponent(ssoId)}`;
          window.open(downloadUrl, '_blank');
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Failed to retrieve auth token for download',
            'gtw-snackbar--red',
          );
        }
        this.exportDone();
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar(
          'Error retrieving auth token. Please try again.',
          'gtw-snackbar--red',
        );
        this.exportDone();
      },
    });
  }

  private exportDone(): void {
    this.hideExportSpinner();
    this.workspaceService.downloadExcelDone.next();
  }

  /** Host callback after a delegated export (`delegateExcelExport`) completes. */
  finishExcelExport(): void {
    this.exportDone();
  }

  private exportToExcel(): void {
    const params = this.buildExportParams();
    if (!params) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    this.showExportSpinner();
    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen } = params;

    // At least one of actionId / actionCode must be present, and gridId is required.
    if ((!actionId && !actionCode) || !gridId) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    const baseUrl = environment.base_url;
    const legacyCustom = this.isLegacyCustomGridExport();
    const legacyGridTpl = this.isLegacyGridTplExport();
    const actionParam = legacyCustom || legacyGridTpl || actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : `action_code=${encodeURIComponent(actionCode)}`;
    const exportUrl = `${baseUrl}/exportToExcel?${actionParam}`
      + `&grid_id=${encodeURIComponent(gridId)}`
      + `&sso_id=${encodeURIComponent(ssoId)}`
      + `&screen_key=${encodeURIComponent(screenKey)}`;
    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] =
      exportData?.headerArray ||
      this.lastLoadedData?.header ||
      activeScreen?.data?.header ||
      [];
    const normalizedHeaders = headerArray.map((item: any) => ({
      object_id: item.object_id,
      label: item.label ?? null,
      data_type: item.data_type ?? null,
      format: item.format ?? null,
      map: item.map,
      width: item.width,
    }));

    const requestJsonObj = legacyCustom || legacyGridTpl
      ? jsonData
      : Object.keys(jsonData).reduce((acc: Record<string, unknown>, key: string) => {
          if (!key.startsWith('display_')) {
            acc[key] = jsonData[key];
          }
          return acc;
        }, {});
    const body =
      `jsonObj=${encodeURIComponent(JSON.stringify(requestJsonObj))}` +
      `&headerArray=${encodeURIComponent(JSON.stringify(normalizedHeaders))}`;

    this.http.post<any>(exportUrl, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
      withCredentials: true,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => this.handleExportResponse(data, ssoId),
      error: () => {
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while exporting. Please try again.',
          'gtw-snackbar--red',
        );
        this.exportDone();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Export to Excel — Download Filtered Data (parity with core GridDirective)
  // ---------------------------------------------------------------------------

  /** Legacy EfileElementsController.isFiltered() — colSearch only. */
  private isFilteredExportAvailable(): boolean {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    const colSearch: any[] =
      this.lastGridDataPayload?.tableState?.colSearch
      || activeScreen?.data?.tableState?.colSearch
      || [];
    return colSearch.length > 0;
  }

  private syncFilteredExportVisibility(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid?.length) {
      return;
    }
    const show = this.isFilteredExportAvailable();
    $grid
      .find('.gg-export-action[action="filtered-export"]')
      .css('display', show ? '' : 'none');
  }

  private exportToExcelFiltered(): void {
    if (!this.isFilteredExportAvailable()) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    const params = this.buildExportParams();
    if (!params) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    this.showExportSpinner();

    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen } = params;
    if ((!actionId && !actionCode) || !gridId) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    const baseUrl = environment.base_url;

    const colSearch: any[] = this.lastGridDataPayload?.tableState?.colSearch || [];
    colSearch.forEach((col: any) => {
      if (col.predicate && col.value != null) {
        jsonData['colFilter_' + col.predicate] = col.value.toString();
      }
    });

    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] =
      exportData?.headerArray ||
      activeScreen?.data?.header ||
      this.lastLoadedData?.header ||
      [];
    const dataArr: any[] = exportData?.dataArr || [];
    const gridSettings: string = exportData?.settings ? JSON.stringify(exportData.settings) : '';

    const headArra: any[] = [];
    const filteredMap: string[] = [];
    headerArray.forEach((item: any) => {
      headArra.push({
        object_id: item.object_id,
        label: item.label,
        data_type: item.data_type,
        format: item.format,
        map: item.map,
        width: item.width,
      });
      filteredMap.push(item.map);
    });

    const isImportScreen = !actionId && (
      jsonData.screen_type === 'grid-import' ||
      jsonData.screen_type === 'grid-import-pivot' ||
      jsonData.screen_type === 'grid-import-td'
    );

    const legacyCustom = this.isLegacyCustomGridExport();
    const legacyGridTpl = this.isLegacyGridTplExport();
    const actionParam = legacyCustom || legacyGridTpl || actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : `action_code=${encodeURIComponent(actionCode)}`;
    const jsonObj = JSON.stringify(jsonData);
    let exportUrl = `${baseUrl}/exportToExcel?${actionParam}`
      + `&grid_id=${encodeURIComponent(gridId)}`
      + `&sso_id=${encodeURIComponent(ssoId)}`
      + `&screen_key=${encodeURIComponent(screenKey)}`;

    if (dataArr.length > 50000 && !isImportScreen) {
      exportUrl += '&is_compressed=false&filtered_export=false';
      const body = `jsonObj=${encodeURIComponent(jsonObj)}`
        + `&headerArray=${encodeURIComponent(JSON.stringify(headArra))}`
        + `&gridSettings=${encodeURIComponent(gridSettings)}`;

      this.http.post<any>(exportUrl, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
        withCredentials: true,
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data: any) => this.handleExportResponse(data, ssoId),
        error: () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red',
          );
          this.exportDone();
        },
      });
    } else {
      exportUrl += '&is_compressed=true&filtered_export=true';

      const gridObject: any = { header: [filteredMap], data: [] as any[] };
      dataArr.forEach((obj: any) => {
        const row: any[] = [];
        filteredMap.forEach((map: string) => {
          row.push(obj[map] === undefined ? null : obj[map]);
        });
        gridObject.data.push(row);
      });

      const compressedData = JSON.stringify(gridObject);
      const blob = new Blob([compressedData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const fd = new FormData();
      fd.append('file', blob);
      fd.append('jsonObj', jsonObj);
      fd.append('headerArray', JSON.stringify(headArra));
      fd.append('gridSettings', gridSettings);

      this.http.post<any>(exportUrl, fd, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (data: any) => this.handleExportResponse(data, ssoId),
        error: () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red',
          );
          this.exportDone();
        },
      });
    }
  }

  private showModalOpenError(message?: string): void {
    this.gtwSnackbarService.openSnackBar(
      message || 'Unable to open the modal. Please try again.',
      'gtw-snackbar--red',
    );
  }
}
